import {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
  useMemo,
  useContext,
  createContext,
  memo,
} from 'react';
import './App.css';
import {
  uploadPhoto as cloudUploadPhoto,
  cloudinaryConfigured,
  cloudinaryRender,
} from './config/cloudinary';
import {
  uploadAV as cloudUploadAV,
  deleteAV as cloudDeleteAV,
  r2Configured,
} from './config/r2Upload';
import {
  cloudSyncConfigured,
  pullTree as cloudPullTree,
  schedulePush as cloudSchedulePush,
  flushPush as cloudFlushPush,
  getLastSyncTs as cloudGetLastSyncTs,
  setLastSyncTs as cloudSetLastSyncTs,
} from './sync/cloudSync';
import {
  countIdbRefs,
  migrateTreeIdbToCloud,
} from './sync/cloudMigration';
import { AuthProvider, useAuth } from './auth/AuthContext';
import LoginPage, { NotInvitedPage, SetPasswordPage } from './auth/LoginPage';
import { supabase } from './config/supabaseClient';

const MAIN_STORAGE_KEY = 'gia-pha-tree-v2';
const MAIN_STORAGE_KEY_V1 = 'gia-pha-tree-v1';
const LEGACY_MAIN_ME_KEY = 'gia-pha-me-id';
const PORTAL_TREE_PREFIX = 'gia-pha-tree-portal-';
const PORTAL_SEED_PREFIX = 'gia-pha-portal-seed-';
const MODE_STORAGE_KEY = 'gia-pha-mode';
// Me is global, not per-tree: a single (id, tree) tuple identifies "the user"
// across every tree they navigate.
const GLOBAL_ME_ID_KEY = 'gia-pha-global-me-id';
const GLOBAL_ME_TREE_KEY = 'gia-pha-global-me-tree';
const MIN_ZOOM = 0.15;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 1.2;
const FIT_PADDING = 48;
const HEART_HALF = 16;
const HEART_FOCUS_ZOOM = 0.75;
const HEART_FOCUS_FLAG = 'gia-pha-focus-me';

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const uuid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const ModeContext = createContext('view');

// --- Media storage (IndexedDB) ---
// Photos / audio / video files live in IndexedDB to escape the ~5 MB
// localStorage cap. The tree itself stores `idb:<id>` reference strings;
// render sites resolve them to object URLs via useMediaUrl.

const IDB_NAME = 'gia-pha-media';
const IDB_STORE = 'media';
const IDB_REF_PREFIX = 'idb:';
let _idbPromise = null;

function openMediaDb() {
  if (_idbPromise) return _idbPromise;
  _idbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _idbPromise;
}

async function idbPut(id, blob) {
  const db = await openMediaDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(blob, id);
    tx.oncomplete = () => resolve(id);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

async function idbGet(id) {
  const db = await openMediaDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const req = tx.objectStore(IDB_STORE).get(id);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

async function idbDelete(ids) {
  const list = Array.isArray(ids) ? ids : [ids];
  if (list.length === 0) return;
  const db = await openMediaDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    const store = tx.objectStore(IDB_STORE);
    for (const id of list) store.delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

async function idbAllEntries() {
  const db = await openMediaDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const out = {};
    const req = tx.objectStore(IDB_STORE).openCursor();
    req.onsuccess = (e) => {
      const cur = e.target.result;
      if (cur) {
        out[cur.key] = cur.value;
        cur.continue();
      } else {
        resolve(out);
      }
    };
    req.onerror = () => reject(req.error);
  });
}

const isIdbRef = (s) => typeof s === 'string' && s.startsWith(IDB_REF_PREFIX);
const isDataUrl = (s) => typeof s === 'string' && s.startsWith('data:');
const makeIdbRef = (id) => IDB_REF_PREFIX + id;
const idFromIdbRef = (ref) => ref.slice(IDB_REF_PREFIX.length);

async function storeBlobInIdb(blob) {
  const id = uuid();
  await idbPut(id, blob);
  return makeIdbRef(id);
}

// Cloud-first upload helpers. Try the configured cloud service; on failure
// (no network, service down, env vars missing) fall back to IndexedDB so the
// user never loses an upload. The migration step will later retry IDB-bound
// items into the cloud once connectivity returns.
async function uploadPhotoOrLocal(blob, folder) {
  if (!cloudinaryConfigured) {
    console.warn(
      '[gia-pha] Cloudinary NOT configured — REACT_APP_CLOUDINARY_CLOUD_NAME or REACT_APP_CLOUDINARY_UPLOAD_PRESET missing. Photo saved to IndexedDB only. Restart the dev server after editing .env.local.'
    );
    return storeBlobInIdb(blob);
  }
  try {
    const result = await cloudUploadPhoto(blob, { folder });
    return result.url;
  } catch (e) {
    console.error('[gia-pha] Cloudinary upload threw, falling back to IndexedDB:', e);
    return storeBlobInIdb(blob);
  }
}

// Returns { src, key } — `key` is the R2 object key when the file landed in
// the cloud (used later for cloud-side deletion), `null` for IDB fallbacks.
async function uploadAVOrLocal(file, kind) {
  if (!r2Configured) {
    console.warn(
      '[gia-pha] R2 NOT configured — REACT_APP_R2_PRESIGN_URL missing. ' +
        kind +
        ' saved to IndexedDB only. Restart the dev server after editing .env.local.'
    );
    return { src: await storeBlobInIdb(file), key: null };
  }
  try {
    const result = await cloudUploadAV(file, kind);
    return { src: result.url, key: result.key };
  } catch (e) {
    console.error(`[gia-pha] R2 ${kind} upload threw, falling back to IndexedDB:`, e);
    return { src: await storeBlobInIdb(file), key: null };
  }
}

function dataUrlToBlob(dataUrl) {
  return fetch(dataUrl).then((r) => r.blob());
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

// Walk a tree, find any string `src` / `photo` / `thumbnail` that is still a
// data: URL, move it to IndexedDB, and return the rewritten tree. Idempotent
// for trees that are already migrated.
async function migrateTreeMediaToIdb(tree) {
  let changed = false;
  const move = async (s) => {
    if (!isDataUrl(s)) return s;
    try {
      const blob = await dataUrlToBlob(s);
      const ref = await storeBlobInIdb(blob);
      changed = true;
      return ref;
    } catch (e) {
      return s;
    }
  };
  const walkMedia = async (media) => {
    if (!media) return media;
    const out = { ...media };
    if (Array.isArray(media.photos)) {
      out.photos = [];
      for (const p of media.photos) out.photos.push({ ...p, src: await move(p.src) });
    }
    if (Array.isArray(media.audio)) {
      out.audio = [];
      for (const a of media.audio) out.audio.push({ ...a, src: await move(a.src) });
    }
    if (Array.isArray(media.videos)) {
      out.videos = [];
      for (const v of media.videos) {
        const nv = { ...v, src: await move(v.src) };
        if (v.thumbnail) nv.thumbnail = await move(v.thumbnail);
        out.videos.push(nv);
      }
    }
    return out;
  };
  const walkPerson = async (p) => {
    const out = { ...p };
    if (p.photo) out.photo = await move(p.photo);
    if (p.media) out.media = await walkMedia(p.media);
    out.marriages = [];
    for (const m of (p.marriages || [])) out.marriages.push(await walkMarriage(m));
    return out;
  };
  const walkMarriage = async (m) => {
    const out = { ...m };
    if (m.media) out.media = await walkMedia(m.media);
    out.spouse = await walkPerson(m.spouse);
    out.children = [];
    for (const c of (m.children || [])) out.children.push(await walkPerson(c));
    return out;
  };
  const result = await walkPerson(tree);
  return { tree: result, changed };
}

// Collect every IDB ref reachable from a person subtree. Used for cascade
// delete when a person or marriage is removed.
function collectIdbRefsInPerson(person, out = []) {
  if (isIdbRef(person.photo)) out.push(idFromIdbRef(person.photo));
  collectIdbRefsInMedia(person.media, out);
  for (const m of (person.marriages || [])) collectIdbRefsInMarriage(m, out);
  return out;
}

function collectIdbRefsInMarriage(marriage, out = []) {
  collectIdbRefsInMedia(marriage.media, out);
  if (marriage.spouse) collectIdbRefsInPerson(marriage.spouse, out);
  for (const c of (marriage.children || [])) collectIdbRefsInPerson(c, out);
  return out;
}

function collectIdbRefsInMedia(media, out = []) {
  if (!media) return out;
  for (const p of (media.photos || [])) if (isIdbRef(p.src)) out.push(idFromIdbRef(p.src));
  for (const a of (media.audio || [])) if (isIdbRef(a.src)) out.push(idFromIdbRef(a.src));
  for (const v of (media.videos || [])) {
    if (isIdbRef(v.src)) out.push(idFromIdbRef(v.src));
    if (isIdbRef(v.thumbnail)) out.push(idFromIdbRef(v.thumbnail));
  }
  return out;
}

// Cheap check: does this tree contain any data: URL still embedded inline?
// Used to skip the async migration walk when there's nothing to do.
function treeHasDataUrls(person) {
  if (isDataUrl(person.photo)) return true;
  if (mediaHasDataUrls(person.media)) return true;
  for (const m of (person.marriages || [])) {
    if (mediaHasDataUrls(m.media)) return true;
    if (treeHasDataUrls(m.spouse)) return true;
    for (const c of (m.children || [])) if (treeHasDataUrls(c)) return true;
  }
  return false;
}

function mediaHasDataUrls(media) {
  if (!media) return false;
  for (const p of (media.photos || [])) if (isDataUrl(p.src)) return true;
  for (const a of (media.audio || [])) if (isDataUrl(a.src)) return true;
  for (const v of (media.videos || [])) {
    if (isDataUrl(v.src) || isDataUrl(v.thumbnail)) return true;
  }
  return false;
}

// Resolve any src — plain URL, data: URL, or idb:<id> ref — to a string usable
// as an <img>/<audio>/<video> src. For idb refs the blob is fetched and a
// short-lived object URL is returned; the URL is revoked on unmount or when
// the src changes.
// For HTTP(S) URLs, run them through cloudinaryRender so Cloudinary delivery
// URLs pick up f_auto/q_auto/dpr_auto. Non-Cloudinary URLs (R2, YouTube) are
// returned unchanged.
function resolvePlainSrc(src) {
  if (typeof src !== 'string') return '';
  if (src.startsWith('http://') || src.startsWith('https://')) {
    return cloudinaryRender(src);
  }
  return src;
}

function useMediaUrl(src) {
  const [resolved, setResolved] = useState(() =>
    isIdbRef(src) ? '' : resolvePlainSrc(src)
  );
  useEffect(() => {
    if (!src) { setResolved(''); return; }
    if (!isIdbRef(src)) { setResolved(resolvePlainSrc(src)); return; }
    let cancelled = false;
    let createdUrl = null;
    idbGet(idFromIdbRef(src))
      .then((blob) => {
        if (cancelled) return;
        if (!blob) { setResolved(''); return; }
        createdUrl = URL.createObjectURL(blob);
        setResolved(createdUrl);
      })
      .catch(() => { if (!cancelled) setResolved(''); });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [src]);
  return resolved;
}

function MediaImg({ src, alt = '', ...rest }) {
  const url = useMediaUrl(src);
  // loading=lazy handles below-the-fold portrait/album/thumbnail images;
  // the browser's heuristic skips it for above-the-fold ones automatically.
  return url ? <img src={url} alt={alt} loading="lazy" {...rest} /> : null;
}

function MediaAudio({ src, ...rest }) {
  const url = useMediaUrl(src);
  return url ? <audio src={url} {...rest} /> : null;
}

function MediaVideo({ src, ...rest }) {
  const url = useMediaUrl(src);
  return url ? <video src={url} {...rest} /> : null;
}

// --- Data model ---

function ensureStory(story) {
  if (typeof story === 'string') return { vi: story, en: '' };
  if (!story || typeof story !== 'object') return { vi: '', en: '' };
  return { vi: story.vi || '', en: story.en || '' };
}

function ensureMedia(media) {
  return {
    photos: media?.photos || [],
    audio: media?.audio || [],
    videos: media?.videos || [],
    story: ensureStory(media?.story),
  };
}

function stripHtml(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || '').trim();
}

function escapeHtml(text) {
  return (text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

async function translateChunk(text, from, to) {
  const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${from}|${to}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`translation HTTP ${res.status}`);
  const data = await res.json();
  return data.responseData?.translatedText || '';
}

async function translateText(text, from, to) {
  const MAX = 480;
  if (text.length <= MAX) return translateChunk(text, from, to);
  // Chunk on sentence/word boundaries to stay under MyMemory's per-call limit.
  const chunks = [];
  let rest = text;
  while (rest.length > 0) {
    if (rest.length <= MAX) { chunks.push(rest); break; }
    let cut = rest.lastIndexOf('. ', MAX);
    if (cut < 100) cut = rest.lastIndexOf(' ', MAX);
    if (cut < 0) cut = MAX;
    chunks.push(rest.slice(0, cut + 1));
    rest = rest.slice(cut + 1);
  }
  const out = [];
  for (const chunk of chunks) {
    out.push(await translateChunk(chunk, from, to));
  }
  return out.join(' ');
}

const newPerson = (overrides = {}) => ({
  id: uuid(),
  name: '',
  birth: '',
  death: '',
  photo: '',
  isPortal: false,
  gender: null, // 'male' | 'female' | null (unset)
  marriages: [],
  ...overrides,
});

const newMarriage = () => ({
  id: uuid(),
  marriageYear: '',
  spouse: newPerson(),
  children: [],
  media: ensureMedia(),
  ended: false,
});

function migrateCouple(c) {
  return {
    id: c.id || uuid(),
    name: c.husband?.name || '',
    birth: c.husband?.birth || '',
    death: c.husband?.death || '',
    photo: c.husband?.photo || '',
    isPortal: false,
    marriages: [
      {
        id: uuid(),
        marriageYear: c.marriageYear || '',
        spouse: {
          id: uuid(),
          name: c.wife?.name || '',
          birth: c.wife?.birth || '',
          death: c.wife?.death || '',
          photo: c.wife?.photo || '',
          isPortal: false,
          marriages: [],
        },
        children: (c.children || []).map(migrateCouple),
        media: ensureMedia(),
      },
    ],
  };
}

function loadStoredTree(storageKey) {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) return JSON.parse(stored);
  } catch (e) {}
  return null;
}

function ensureTreeMedia(person) {
  const withPersonMedia = mapPersons(person, (p) => ({
    ...p,
    media: ensureMedia(p.media),
  }));
  return mapMarriages(withPersonMedia, (m) => ({
    ...m,
    media: ensureMedia(m.media),
  }));
}

// --- Admin log ---

const ADMIN_LOG_KEY = 'gia-pha-admin-log';
const ADMIN_LOG_LIMIT = 500;

function loadAdminLog() {
  try {
    const v = localStorage.getItem(ADMIN_LOG_KEY);
    return v ? JSON.parse(v) : [];
  } catch (e) {
    return [];
  }
}

function adminLogEntryKey(entry) {
  return JSON.stringify({
    timestamp: entry?.timestamp || '',
    actor: entry?.actor || '',
    action: entry?.action || '',
    target: entry?.target || null,
    details: entry?.details || null,
  });
}

function normalizeAdminLog(log) {
  if (!Array.isArray(log)) return [];
  const seen = new Set();
  const out = [];
  for (const entry of log) {
    if (!entry || typeof entry !== 'object') continue;
    const normalized = {
      ...entry,
      timestamp: entry.timestamp || new Date().toISOString(),
    };
    const key = adminLogEntryKey(normalized);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(normalized);
  }
  out.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  if (out.length > ADMIN_LOG_LIMIT) out.length = ADMIN_LOG_LIMIT;
  return out;
}

function saveAdminLog(log, { push = true } = {}) {
  const normalized = normalizeAdminLog(log);
  try {
    localStorage.setItem(ADMIN_LOG_KEY, JSON.stringify(normalized));
    if (push && cloudSyncConfigured) {
      cloudSchedulePush(ADMIN_LOG_KEY, normalized);
    }
  } catch (e) {}
  return normalized;
}

function mergeAdminLogs(a, b) {
  return normalizeAdminLog([...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : [])]);
}

function appendAdminLog(entry) {
  try {
    saveAdminLog([
      {
        ...entry,
        timestamp: entry.timestamp || new Date().toISOString(),
      },
      ...loadAdminLog(),
    ]);
  } catch (e) {}
}

function clearAdminLog() {
  try { saveAdminLog([]); } catch (e) {}
}

const LogContext = createContext(() => {});

// --- Backup / restore (the actual answer to "don't lose data") ---

const LAST_BACKUP_KEY = 'gia-pha-last-backup';
const BACKUP_STALE_DAYS = 7;

const IDB_BACKUP_KEY = '__gia_pha_idb_media__';

async function exportAllGiaPha() {
  const data = {};
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith('gia-pha-')) data[k] = localStorage.getItem(k);
  }
  // Bundle IndexedDB media as base64 data URLs so the backup is a single JSON
  // file. On import we re-inflate them back into IDB.
  let idbBlobs = {};
  try {
    idbBlobs = await idbAllEntries();
  } catch (e) { idbBlobs = {}; }
  const idbDataUrls = {};
  for (const [id, blob] of Object.entries(idbBlobs)) {
    try {
      idbDataUrls[id] = await blobToDataUrl(blob);
    } catch (e) {}
  }
  const payload = { ...data, [IDB_BACKUP_KEY]: idbDataUrls };
  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const stamp = new Date()
    .toISOString()
    .replace(/[:.]/g, '-')
    .slice(0, 19);
  const a = document.createElement('a');
  a.href = url;
  a.download = `gia-pha-backup-${stamp}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  try {
    localStorage.setItem(LAST_BACKUP_KEY, new Date().toISOString());
  } catch (e) {}
  return Object.keys(data).length + Object.keys(idbDataUrls).length;
}

async function importGiaPhaBackup(file) {
  const text = await file.text();
  const data = JSON.parse(text);
  if (!data || typeof data !== 'object') throw new Error('Backup is not a JSON object.');
  let restored = 0;
  for (const [k, v] of Object.entries(data)) {
    if (k === IDB_BACKUP_KEY) continue;
    if (typeof k === 'string' && k.startsWith('gia-pha-') && typeof v === 'string') {
      localStorage.setItem(k, v);
      restored++;
    }
  }
  const idbDataUrls = data[IDB_BACKUP_KEY];
  if (idbDataUrls && typeof idbDataUrls === 'object') {
    for (const [id, dataUrl] of Object.entries(idbDataUrls)) {
      if (typeof dataUrl !== 'string') continue;
      try {
        const blob = await dataUrlToBlob(dataUrl);
        await idbPut(id, blob);
        restored++;
      } catch (e) {}
    }
  }
  return restored;
}

function isBackupStale() {
  const last = localStorage.getItem(LAST_BACKUP_KEY);
  if (!last) return true;
  const ms = Date.now() - new Date(last).getTime();
  return ms > BACKUP_STALE_DAYS * 24 * 60 * 60 * 1000;
}

// Person fields that describe the individual (vs. their position in a
// particular tree). When the same person appears in multiple trees — e.g. as
// a spouse in their family-of-origin and as the root of their own portal —
// edits to these fields propagate across all trees that reference them.
// Marriages, children, and isPortal stay tree-local.
const SYNCED_PERSON_FIELDS = ['name', 'birth', 'death', 'photo', 'gender', 'media', 'auth'];

function pickSyncedPersonFields(person) {
  const out = {};
  for (const k of SYNCED_PERSON_FIELDS) {
    if (k in person) out[k] = person[k];
  }
  return out;
}

function listAllTreeStorageKeys() {
  const keys = [];
  if (localStorage.getItem(MAIN_STORAGE_KEY) !== null) keys.push(MAIN_STORAGE_KEY);
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(PORTAL_TREE_PREFIX)) keys.push(k);
  }
  return keys;
}

function syncPersonAcrossTrees(personId, sourceStorageKey, syncedFields) {
  for (const k of listAllTreeStorageKeys()) {
    if (k === sourceStorageKey) continue;
    const tree = loadStoredTree(k);
    if (!tree || !findPerson(tree, personId)) continue;
    const updated = mapPersons(tree, (p) =>
      p.id === personId ? { ...p, ...syncedFields } : p
    );
    try {
      localStorage.setItem(k, JSON.stringify(updated));
      if (cloudSyncConfigured) cloudSchedulePush(k, updated);
    } catch (e) {}
  }
}

// Marriage fields that describe the marriage itself rather than its position
// in a particular tree. spouse and children are deliberately tree-local: the
// same marriage M between A and B is hung off A in A's tree and off B in B's
// portal tree, so the spouse/children arrays differ structurally.
const SYNCED_MARRIAGE_FIELDS = ['marriageYear', 'ended', 'media'];

function pickSyncedMarriageFields(marriage) {
  const out = {};
  for (const k of SYNCED_MARRIAGE_FIELDS) {
    if (k in marriage) out[k] = marriage[k];
  }
  return out;
}

function syncMarriageAcrossTrees(marriageId, sourceStorageKey, syncedFields) {
  for (const k of listAllTreeStorageKeys()) {
    if (k === sourceStorageKey) continue;
    const tree = loadStoredTree(k);
    if (!tree || !findMarriage(tree, marriageId)) continue;
    const updated = mapMarriages(tree, (m) =>
      m.id === marriageId ? { ...m, ...syncedFields } : m
    );
    try {
      localStorage.setItem(k, JSON.stringify(updated));
      if (cloudSyncConfigured) cloudSchedulePush(k, updated);
    } catch (e) {}
  }
}

function youtubeIdFromUrl(url) {
  if (!url) return null;
  const re = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
  const m = url.match(re);
  return m ? m[1] : null;
}

// A person is deceased if their death field carries a date (any 2+ consecutive
// digits, e.g. 1962, 15/03/1962, ~1900) or one of the explicit tokens
// "Deceased" / "Unknown DOD" (case-insensitive). Other free text does NOT
// count — adding a memorial to the living is taboo.
function isDeceasedFromField(death) {
  const t = (death || '').trim();
  if (!t) return false;
  const lower = t.toLowerCase();
  if (lower.includes('deceased') || lower.includes('unknown dod')) return true;
  if (/\d{2,}/.test(t)) return true;
  return false;
}

function loadMainTree() {
  const v2 = loadStoredTree(MAIN_STORAGE_KEY);
  if (v2) return ensureTreeMedia(v2);
  const v1 = loadStoredTree(MAIN_STORAGE_KEY_V1);
  if (v1) return ensureTreeMedia(migrateCouple(v1));
  const root = newPerson();
  root.marriages.push(newMarriage());
  return root;
}

function loadPortalTree(portalId) {
  const stored = loadStoredTree(PORTAL_TREE_PREFIX + portalId);
  if (stored) return ensureTreeMedia(stored);
  let seed = {};
  try {
    const raw = localStorage.getItem(PORTAL_SEED_PREFIX + portalId);
    if (raw) seed = JSON.parse(raw);
  } catch (e) {}

  // Spread every persistable person field captured by the source tree.
  // newPerson supplies defaults for anything missing (e.g. an older seed
  // written before this expansion).
  const root = newPerson({ id: portalId, ...seed });
  // reverseMarriage is metadata for bootstrapping, not a person field.
  delete root.reverseMarriage;
  root.marriages = [];

  // If the seed has a "reverse marriage", it means we're entering a spouse's
  // portal from the source tree. Pre-populate that marriage with the partner's
  // existing id and the shared children's existing ids — so leaf clicks here
  // route to the same portals instead of creating duplicates.
  if (seed.reverseMarriage) {
    const rm = seed.reverseMarriage;
    const marriage = newMarriage();
    marriage.marriageYear = rm.marriageYear || '';
    marriage.ended = !!rm.ended;
    if (rm.media) marriage.media = ensureMedia(rm.media);
    marriage.spouse = newPerson({
      id: rm.partnerId || rm.partner?.id || uuid(),
      ...(rm.partner || {}),
    });
    marriage.children = (rm.children || []).map((c) =>
      newPerson({ id: c.id || uuid(), ...c })
    );
    root.marriages.push(marriage);
  } else {
    root.marriages.push(newMarriage());
  }
  return ensureTreeMedia(root);
}

function loadGlobalMe() {
  let id = localStorage.getItem(GLOBAL_ME_ID_KEY);
  let tree = localStorage.getItem(GLOBAL_ME_TREE_KEY);
  if (!id) {
    // One-time migration: the old per-tree main Me key was the user's identity
    // before this refactor; treat it as a global Me anchored to the main tree.
    const legacy = localStorage.getItem(LEGACY_MAIN_ME_KEY);
    if (legacy && legacy !== '') {
      id = legacy;
      tree = 'main';
    }
  }
  if (!id) return { id: null, tree: null };
  return { id, tree: tree || 'main' };
}

function saveGlobalMe(me) {
  if (me?.id) {
    localStorage.setItem(GLOBAL_ME_ID_KEY, me.id);
    localStorage.setItem(GLOBAL_ME_TREE_KEY, me.tree || 'main');
  } else {
    localStorage.removeItem(GLOBAL_ME_ID_KEY);
    localStorage.removeItem(GLOBAL_ME_TREE_KEY);
  }
}

function storageKeyForTree(treeKey) {
  if (!treeKey || treeKey === 'main') return MAIN_STORAGE_KEY;
  if (treeKey.startsWith('portal/')) return PORTAL_TREE_PREFIX + treeKey.slice(7);
  return null;
}

function lookupMeName(me) {
  if (!me?.id || !me?.tree) return '';
  const key = storageKeyForTree(me.tree);
  if (!key) return '';
  const stored = loadStoredTree(key);
  if (!stored) return '';
  return findPerson(stored, me.id)?.name?.trim() || '';
}

function navigateToTreeKey(treeKey) {
  if (!treeKey || treeKey === 'main') {
    navigateToMain();
  } else if (treeKey.startsWith('portal/')) {
    navigateToPortal(treeKey.slice(7));
  }
}

function mapPersons(person, fn) {
  const next = fn(person);
  return {
    ...next,
    marriages: next.marriages.map((m) => ({
      ...m,
      spouse: mapPersons(m.spouse, fn),
      children: m.children.map((c) => mapPersons(c, fn)),
    })),
  };
}

function mapMarriages(person, fn) {
  return {
    ...person,
    marriages: person.marriages.map((m) => {
      const transformed = fn(m);
      return {
        ...transformed,
        spouse: mapMarriages(transformed.spouse, fn),
        children: transformed.children.map((c) => mapMarriages(c, fn)),
      };
    }),
  };
}

function findPerson(person, targetId) {
  if (person.id === targetId) return person;
  for (const m of person.marriages) {
    if (m.spouse.id === targetId) return m.spouse;
    for (const c of m.children) {
      const found = findPerson(c, targetId);
      if (found) return found;
    }
  }
  return null;
}

// Build a "X & Y" label for a marriage by finding its lineage parent.
// Used by the audit log so entries like "Aunt Mary uploaded audio" gain the
// missing context — "to the marriage between Vinh & Duong".
function findMarriageContext(root, marriageId) {
  if (!root) return null;
  for (const m of root.marriages || []) {
    if (m.id === marriageId) return { marriage: m, lineageParent: root };
    const inSpouse = findMarriageContext(m.spouse, marriageId);
    if (inSpouse) return inSpouse;
    for (const c of m.children || []) {
      const inChild = findMarriageContext(c, marriageId);
      if (inChild) return inChild;
    }
  }
  return null;
}

function marriageDisplayName(tree, marriageId) {
  const ctx = findMarriageContext(tree, marriageId);
  if (!ctx) return '';
  const parentName = ctx.lineageParent?.name?.trim() || 'unnamed';
  const spouseName = ctx.marriage?.spouse?.name?.trim() || 'unnamed';
  return `${parentName} & ${spouseName}`;
}

function findMarriage(person, marriageId) {
  for (const m of person.marriages) {
    if (m.id === marriageId) return m;
    const inSpouse = findMarriage(m.spouse, marriageId);
    if (inSpouse) return inSpouse;
    for (const c of m.children) {
      const found = findMarriage(c, marriageId);
      if (found) return found;
    }
  }
  return null;
}


// --- Delete-impact accounting ---
// Tallies people, photos, audio clips, videos, and story pages reachable from
// a node about to be removed. Used by the impact-preview modal so users see
// exactly what they will lose before confirming a destructive action.

function emptyImpact() {
  return { people: 0, photos: 0, audio: 0, videos: 0, stories: 0 };
}

function addMediaToImpact(media, acc) {
  if (!media) return;
  acc.photos += (media.photos || []).length;
  acc.audio += (media.audio || []).length;
  acc.videos += (media.videos || []).length;
  const story = media.story || {};
  if (
    stripHtml(story.vi || '').length > 0 ||
    stripHtml(story.en || '').length > 0
  ) {
    acc.stories += 1;
  }
}

function walkPersonForImpact(person, acc) {
  if (!person) return;
  acc.people += 1;
  if (person.media) addMediaToImpact(person.media, acc);
  for (const m of person.marriages || []) {
    if (m.media) addMediaToImpact(m.media, acc);
    walkPersonForImpact(m.spouse, acc);
    for (const c of m.children || []) walkPersonForImpact(c, acc);
  }
}

// Impact of removing a person node (descendants are pruned with them).
function impactOfPersonRemoval(person) {
  const acc = emptyImpact();
  walkPersonForImpact(person, acc);
  return acc;
}

// Impact of removing an entire marriage (spouse + every child subtree).
function impactOfMarriageRemoval(marriage) {
  const acc = emptyImpact();
  if (marriage.media) addMediaToImpact(marriage.media, acc);
  walkPersonForImpact(marriage.spouse, acc);
  for (const c of marriage.children || []) walkPersonForImpact(c, acc);
  return acc;
}

// Replace the spouse object inside a single marriage by id, leaving every
// other field of the marriage (children, media, year) intact. Used by the
// "wrong person" Replace flow so a typo'd spouse can be swapped without
// pruning the children below.
function replaceSpouseInTree(root, marriageId, newSpouse) {
  return {
    ...root,
    marriages: (root.marriages || []).map((m) => {
      if (m.id === marriageId) {
        return { ...m, spouse: newSpouse };
      }
      return {
        ...m,
        spouse: replaceSpouseInTree(m.spouse, marriageId, newSpouse),
        children: (m.children || []).map((c) =>
          replaceSpouseInTree(c, marriageId, newSpouse)
        ),
      };
    }),
  };
}

// Whether the impact modal should require the user to type DELETE to
// confirm. Single-leaf-person-no-media deletions stay on the lightweight
// confirm() path per spec.
function impactNeedsTypedConfirm(impact) {
  return (
    impact.people > 1 ||
    impact.photos > 0 ||
    impact.audio > 0 ||
    impact.videos > 0 ||
    impact.stories > 0
  );
}

// Human-readable bullets for the impact modal: "12 people", "34 photos, 3
// audio clips, 1 video", "5 story pages". Skips zero counts.
function formatImpactBullets(impact) {
  const lines = [];
  if (impact.people > 0) {
    lines.push(`${impact.people} ${impact.people === 1 ? 'person' : 'people'}`);
  }
  const mediaParts = [];
  if (impact.photos > 0) {
    mediaParts.push(`${impact.photos} ${impact.photos === 1 ? 'photo' : 'photos'}`);
  }
  if (impact.audio > 0) {
    mediaParts.push(
      `${impact.audio} ${impact.audio === 1 ? 'audio clip' : 'audio clips'}`
    );
  }
  if (impact.videos > 0) {
    mediaParts.push(`${impact.videos} ${impact.videos === 1 ? 'video' : 'videos'}`);
  }
  if (mediaParts.length) lines.push(mediaParts.join(', '));
  if (impact.stories > 0) {
    lines.push(`${impact.stories} story ${impact.stories === 1 ? 'page' : 'pages'}`);
  }
  return lines;
}

function removePersonFromTree(root, personId) {
  if (root.id === personId) return root;
  return {
    ...root,
    marriages: root.marriages.map((m) => ({
      ...m,
      spouse: removePersonFromTree(m.spouse, personId),
      children: m.children
        .filter((c) => c.id !== personId)
        .map((c) => removePersonFromTree(c, personId)),
    })),
  };
}

function removeMarriageFromTree(root, marriageId) {
  return {
    ...root,
    marriages: root.marriages
      .filter((m) => m.id !== marriageId)
      .map((m) => ({
        ...m,
        spouse: removeMarriageFromTree(m.spouse, marriageId),
        children: m.children.map((c) => removeMarriageFromTree(c, marriageId)),
      })),
  };
}

// --- Routing ---

function parseHash() {
  const h = (window.location.hash || '').replace(/^#/, '');
  if (h.startsWith('portal/')) {
    try {
      return { kind: 'portal', portalId: decodeURIComponent(h.slice('portal/'.length)) };
    } catch (e) {
      return { kind: 'main' };
    }
  }
  return { kind: 'main' };
}

function navigateToPortal(personId) {
  window.location.hash = `portal/${encodeURIComponent(personId)}`;
}

function navigateToMain() {
  window.location.hash = 'main';
}

// --- Icons ---

function HeartSvg({ filled, size = 16, strokeWidth = 2 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path
        d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TrashSvg({ size = 14 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path
        d="M9 3a1 1 0 0 0-1 1v1H4a1 1 0 1 0 0 2h1l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12h1a1 1 0 1 0 0-2h-4V4a1 1 0 0 0-1-1H9zm1 7a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-6a1 1 0 0 1 1-1zm4 0a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-6a1 1 0 0 1 1-1z"
        fill="currentColor"
      />
    </svg>
  );
}

function LeafSvg({ size = 14 }) {
  return (
    <svg viewBox="0 0 256 256" width={size} height={size} aria-hidden="true">
      <path
        d="M223.45,40.07a8,8,0,0,0-7.52-7.52C139.8,28.08,78.82,51,52.82,94a87.09,87.09,0,0,0-12.76,49.6,90,90,0,0,0,9.49,38.06l-26.21,26.2a8,8,0,0,0,11.32,11.32l26.2-26.21a90.06,90.06,0,0,0,38.06,9.49h.51a86.66,86.66,0,0,0,49.09-12.76C191.07,177.18,214,116.21,223.45,40.07Z"
        fill="currentColor"
      />
    </svg>
  );
}

function EyeSvg({ size = 14 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path
        d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7zm0 12c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"
        fill="currentColor"
      />
    </svg>
  );
}

function PencilSvg({ size = 14 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path
        d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
        fill="currentColor"
      />
    </svg>
  );
}

function CameraSvg({ size = 16 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path
        d="M9 3 7.5 5H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-3.5L15 3H9zm3 5a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"
        fill="currentColor"
      />
    </svg>
  );
}

function MusicSvg({ size = 16 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path
        d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z"
        fill="currentColor"
      />
    </svg>
  );
}

function FilmSvg({ size = 16 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path
        d="M4 3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H4zm0 2h2v2H4V5zm14 0h2v2h-2V5zM4 9h2v2H4V9zm14 0h2v2h-2V9zM4 13h2v2H4v-2zm14 0h2v2h-2v-2zM4 17h2v2H4v-2zm14 0h2v2h-2v-2zM8 5h8v6H8V5zm0 8h8v6H8v-6z"
        fill="currentColor"
      />
    </svg>
  );
}

function ScrollSvg({ size = 16 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path
        d="M19 3H8a3 3 0 0 0-3 3v3H3v9a3 3 0 0 0 3 3h13a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3zm0 2a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H8.83A2.99 2.99 0 0 0 9 18V6a1 1 0 0 1 1-1h9zM7 6v3H5V6a1 1 0 1 1 2 0zm0 12V11h2v7a1 1 0 1 1-2 0zM12 8h5v2h-5V8zm0 4h5v2h-5v-2z"
        fill="currentColor"
      />
    </svg>
  );
}

function CloseSvg({ size = 16 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path
        d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
        fill="currentColor"
      />
    </svg>
  );
}

function PlusSvg({ size = 14 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6V5z" fill="currentColor" />
    </svg>
  );
}

function RingSvg({ active, size = 13 }) {
  if (active) {
    // Bold solid rings — clearly "on".
    return (
      <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
        <circle cx="9" cy="14" r="5.2" fill="none" stroke="currentColor" strokeWidth="2.5" />
        <circle cx="15" cy="14" r="5.2" fill="none" stroke="currentColor" strokeWidth="2.5" />
      </svg>
    );
  }
  // Faded thin rings + bold red diagonal slash — clearly "off".
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <circle cx="9" cy="14" r="5.2" fill="none" stroke="currentColor" strokeWidth="1.4" opacity="0.55" />
      <circle cx="15" cy="14" r="5.2" fill="none" stroke="currentColor" strokeWidth="1.4" opacity="0.55" />
      <line x1="2.5" y1="22.5" x2="21.5" y2="4" stroke="#e34d4d" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

function KeySvg({ size = 14 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path
        d="M14 2a6 6 0 0 0-5.66 8H4l-2 2v3h3v-2h2v-2h2v-2h1.34A6 6 0 1 0 14 2zm3 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3z"
        fill="currentColor"
      />
    </svg>
  );
}

function UserSvg({ size = 14 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path
        d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0 2c-3 0-8 1.5-8 4.5V21h16v-2.5c0-3-5-4.5-8-4.5z"
        fill="currentColor"
      />
    </svg>
  );
}

function PersonalMediaSvg({ size = 13 }) {
  // Quiet 2x2 grid suggesting a small personal collection.
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <rect x="3" y="3" width="8" height="8" rx="1.5" fill="currentColor" opacity="0.85" />
      <rect x="13" y="3" width="8" height="8" rx="1.5" fill="currentColor" opacity="0.65" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" fill="currentColor" opacity="0.5" />
      <rect x="13" y="13" width="8" height="8" rx="1.5" fill="currentColor" opacity="0.35" />
    </svg>
  );
}

function AltarIconSvg({ size = 13 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      {/* tablet/portrait on top */}
      <rect x="9" y="2" width="6" height="8" rx="0.6" fill="currentColor" />
      <rect x="9.6" y="2.6" width="4.8" height="6.2" fill="rgba(0,0,0,0.45)" />
      {/* tiers */}
      <rect x="3" y="11" width="18" height="1.6" fill="currentColor" />
      <rect x="5" y="14" width="14" height="1.6" fill="currentColor" opacity="0.85" />
      <rect x="2" y="17" width="20" height="1.6" fill="currentColor" />
      {/* legs */}
      <rect x="4" y="20" width="2.5" height="2" fill="currentColor" />
      <rect x="11" y="20" width="2" height="2" fill="currentColor" />
      <rect x="17.5" y="20" width="2.5" height="2" fill="currentColor" />
    </svg>
  );
}

function CandleSvg() {
  return (
    <svg viewBox="0 0 60 220" className="candle-svg" aria-hidden="true">
      <defs>
        <linearGradient id="candleBody" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#a08560" />
          <stop offset="35%" stopColor="#fff8e0" />
          <stop offset="70%" stopColor="#f0e0b8" />
          <stop offset="100%" stopColor="#9a8055" />
        </linearGradient>
      </defs>
      {/* halo */}
      <ellipse cx="30" cy="22" rx="28" ry="44" fill="rgba(255, 200, 80, 0.18)" className="candle-glow" />
      {/* outer flame */}
      <ellipse cx="30" cy="24" rx="7.5" ry="17" fill="#ffb840" className="candle-flame-outer" />
      {/* inner flame */}
      <ellipse cx="30" cy="28" rx="4.5" ry="12" fill="#ffe480" className="candle-flame-inner" />
      {/* core */}
      <ellipse cx="30" cy="32" rx="2" ry="6.5" fill="#fff8d8" />
      {/* wick */}
      <line x1="30" y1="40" x2="30" y2="48" stroke="#3a2010" strokeWidth="1.5" />
      {/* body */}
      <rect x="22" y="48" width="16" height="148" fill="url(#candleBody)" rx="2" />
      {/* drip highlights */}
      <path d="M22 48 L22 110 L24 122 L24 90 Z" fill="#ffeec8" opacity="0.55" />
      <path d="M38 48 L38 100 L36 115 L36 80 Z" fill="#5a4a30" opacity="0.35" />
      {/* wax drip */}
      <ellipse cx="26" cy="160" rx="2.5" ry="6" fill="#fff5d8" opacity="0.5" />
      {/* holder */}
      <ellipse cx="30" cy="200" rx="17" ry="3.5" fill="#3a2010" />
      <ellipse cx="30" cy="198" rx="15" ry="2.5" fill="#5a3a1a" />
      <ellipse cx="30" cy="196" rx="11" ry="1.5" fill="#7a5530" />
    </svg>
  );
}

function MemorialIncenseSvg() {
  return (
    <svg viewBox="0 0 100 220" className="memorial-incense-svg" aria-hidden="true">
      <defs>
        <linearGradient id="bowlGrad" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#3a2010" />
          <stop offset="40%" stopColor="#7a5530" />
          <stop offset="60%" stopColor="#8a6535" />
          <stop offset="100%" stopColor="#3a2010" />
        </linearGradient>
        <linearGradient id="bowlRim" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#5a3a1a" />
          <stop offset="50%" stopColor="#a07835" />
          <stop offset="100%" stopColor="#5a3a1a" />
        </linearGradient>
      </defs>
      {/* smoke wisps — rising from each ember, staggered */}
      <path
        className="incense-smoke incense-smoke-1"
        d="M40 105 Q32 80 40 55 Q48 30 38 5 Q30 -15 40 -30"
        stroke="rgba(180, 180, 180, 0.55)"
        strokeWidth="1.8"
        fill="none"
        strokeLinecap="round"
      />
      <path
        className="incense-smoke incense-smoke-2"
        d="M50 100 Q60 70 50 40 Q40 10 55 -15 Q70 -35 50 -55"
        stroke="rgba(170, 170, 170, 0.65)"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
      <path
        className="incense-smoke incense-smoke-3"
        d="M60 105 Q70 80 60 55 Q50 30 65 5 Q80 -15 60 -30"
        stroke="rgba(165, 165, 165, 0.5)"
        strokeWidth="1.8"
        fill="none"
        strokeLinecap="round"
      />
      {/* embers */}
      <circle className="incense-ember incense-ember-1" cx="40" cy="108" r="2.2" fill="#ff7a20" />
      <circle className="incense-ember incense-ember-2" cx="50" cy="103" r="2.5" fill="#ff7a20" />
      <circle className="incense-ember incense-ember-3" cx="60" cy="108" r="2.2" fill="#ff7a20" />
      {/* sticks slightly fanned */}
      <line x1="40" y1="108" x2="44" y2="160" stroke="#5a3a1a" strokeWidth="1.8" />
      <line x1="50" y1="103" x2="50" y2="160" stroke="#5a3a1a" strokeWidth="2" />
      <line x1="60" y1="108" x2="56" y2="160" stroke="#5a3a1a" strokeWidth="1.8" />
      {/* bowl */}
      <ellipse cx="50" cy="162" rx="34" ry="4.5" fill="url(#bowlRim)" />
      <path d="M16 162 L20 200 Q50 212 80 200 L84 162 Z" fill="url(#bowlGrad)" />
      <ellipse cx="50" cy="162" rx="32" ry="3.5" fill="#a07835" />
      <ellipse cx="50" cy="162" rx="28" ry="2.5" fill="#5a3a1a" />
      {/* rice/sand pile inside */}
      <ellipse cx="50" cy="160" rx="22" ry="2" fill="#d4c8a8" />
      {/* shine */}
      <ellipse cx="38" cy="170" rx="6" ry="2" fill="rgba(255, 240, 200, 0.4)" />
      {/* table mat shadow */}
      <ellipse cx="50" cy="208" rx="42" ry="5" fill="rgba(0, 0, 0, 0.5)" />
    </svg>
  );
}

function IncenseSvg() {
  return (
    <svg
      viewBox="0 0 36 60"
      width="30"
      height="50"
      className="incense-svg"
      aria-hidden="true"
    >
      {/* Three smoke wisps — staggered for natural drift */}
      <path
        className="incense-smoke incense-smoke-1"
        d="M11 22 Q8 16 11 8 Q14 0 11 -8"
        stroke="rgba(180, 180, 180, 0.55)"
        strokeWidth="1.3"
        fill="none"
        strokeLinecap="round"
      />
      <path
        className="incense-smoke incense-smoke-2"
        d="M18 20 Q15 13 18 5 Q21 -3 18 -11"
        stroke="rgba(170, 170, 170, 0.6)"
        strokeWidth="1.4"
        fill="none"
        strokeLinecap="round"
      />
      <path
        className="incense-smoke incense-smoke-3"
        d="M25 22 Q28 16 25 8 Q22 0 25 -8"
        stroke="rgba(165, 165, 165, 0.5)"
        strokeWidth="1.3"
        fill="none"
        strokeLinecap="round"
      />

      {/* Three embers — slightly different sizes, staggered pulse */}
      <circle className="incense-ember incense-ember-1" cx="11" cy="24" r="1.3" fill="#ff7a20" />
      <circle className="incense-ember incense-ember-2" cx="18" cy="22" r="1.5" fill="#ff7a20" />
      <circle className="incense-ember incense-ember-3" cx="25" cy="24" r="1.3" fill="#ff7a20" />

      {/* Three sticks — gently fanned from a shared bowl */}
      <line x1="11" y1="24" x2="15" y2="50" stroke="#5a3a1a" strokeWidth="1.3" />
      <line x1="18" y1="22" x2="18" y2="50" stroke="#5a3a1a" strokeWidth="1.4" />
      <line x1="25" y1="24" x2="21" y2="50" stroke="#5a3a1a" strokeWidth="1.3" />

      {/* Bowl / holder */}
      <ellipse cx="18" cy="51" rx="7" ry="1.7" fill="#3a2010" />
    </svg>
  );
}

// --- Confirm modal ---

function ConfirmModal({ message, onConfirm, onCancel }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onConfirm, onCancel]);

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <p className="modal-message">{message}</p>
        <div className="modal-actions">
          <button type="button" className="modal-btn modal-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="modal-btn modal-delete"
            onClick={onConfirm}
            autoFocus
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// Hard-stop confirmation when a delete will prune anything bigger than a
// single empty leaf person. Shows the exact damage (people, photos, audio,
// videos, story pages) and forces the user to type DELETE to proceed. The
// Escape/Enter shortcuts intentionally do NOT auto-confirm — typing is the
// only way through.
function DeleteImpactModal({ title, impact, onConfirm, onCancel }) {
  const [typed, setTyped] = useState('');
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);
  const bullets = formatImpactBullets(impact);
  const ready = typed === 'DELETE';
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div
        className="modal modal-delete-impact"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <p className="modal-impact-title">{title}</p>
        {bullets.length > 0 && (
          <ul className="modal-impact-list">
            {bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        )}
        <p className="modal-impact-warning">
          This cannot be undone after 30 seconds.
        </p>
        <label className="modal-impact-typebox">
          <span>Type <strong>DELETE</strong> to confirm:</span>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoFocus
            spellCheck={false}
            autoCapitalize="characters"
          />
        </label>
        <div className="modal-actions">
          <button type="button" className="modal-btn modal-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="modal-btn modal-delete"
            onClick={() => { if (ready) onConfirm(); }}
            disabled={!ready}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// Spouse-card trash now opens a 3-way choice instead of a hard delete:
// • Replace — swap a wrong person without losing children below
// • Edit    — close the modal and use the inline fields
// • Remove  — fall through to the impact-preview modal
function SpouseActionModal({ spouseName, onReplace, onEdit, onRemove, onCancel }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);
  const label = spouseName?.trim() || 'this person';
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div
        className="modal modal-spouse-action"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <p className="modal-message">
          What would you like to do with <strong>{label}</strong>?
        </p>
        <div className="modal-spouse-action-buttons">
          <button
            type="button"
            className="modal-btn modal-spouse-replace"
            onClick={onReplace}
            title="Wrong person — swap them out, keep the children below"
          >
            Replace this person
          </button>
          <button
            type="button"
            className="modal-btn modal-spouse-edit"
            onClick={onEdit}
            title="Just fix their name, dates, or photo"
          >
            Edit their details
          </button>
          <button
            type="button"
            className="modal-btn modal-spouse-remove"
            onClick={onRemove}
            title="Remove the marriage and prune everything below"
          >
            Remove from marriage
          </button>
        </div>
        <div className="modal-actions">
          <button type="button" className="modal-btn modal-cancel" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Bottom-of-screen toast surfacing the most recent destructive action with
// a 30-second window to undo. The toast self-updates every second so the
// countdown stays in sync with the timer.
function UndoToast({ label, expiresAt, onUndo, onDismiss }) {
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000))
  );
  useEffect(() => {
    const tick = () => {
      const s = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setSecondsLeft(s);
      if (s <= 0) onDismiss();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt, onDismiss]);
  return (
    <div className="undo-toast" role="status" aria-live="polite">
      <span className="undo-toast-label">{label}</span>
      <button type="button" className="undo-toast-btn" onClick={onUndo}>
        Undo ({secondsLeft}s)
      </button>
    </div>
  );
}

function CloudConflictModal({ remoteUpdatedAt, onPullCloud, onKeepLocal }) {
  const when = new Date(remoteUpdatedAt).toLocaleString();
  return (
    <div className="modal-backdrop">
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h3 className="modal-title">Newer copy in the cloud</h3>
        <p className="modal-message">
          This tree was last saved from another device on {when}. Your local
          version hasn't seen those changes. Which copy should win?
        </p>
        <div className="modal-actions">
          <button type="button" className="modal-btn modal-cancel" onClick={onKeepLocal}>
            Keep my version
          </button>
          <button
            type="button"
            className="modal-btn modal-confirm"
            onClick={onPullCloud}
            autoFocus
          >
            Pull cloud version
          </button>
        </div>
      </div>
    </div>
  );
}

function MigrationModal({ count, onConfirm, onSkip }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onSkip(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onSkip]);
  const noun = count === 1 ? 'item' : 'items';
  return (
    <div className="modal-backdrop" onClick={onSkip}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h3 className="modal-title">Move local media to the cloud?</h3>
        <p className="modal-message">
          This tree has {count} media {noun} (photos, audio, or video) saved
          only on this device. Uploading them now means they'll show up on
          your phone and other browsers, and they'll be included in cloud
          backups.
        </p>
        <p className="modal-message" style={{ opacity: 0.7, fontSize: 13 }}>
          You can keep editing while it runs. Failures are safe — anything
          that doesn't upload stays right where it is for next time.
        </p>
        <div className="modal-actions">
          <button type="button" className="modal-btn modal-cancel" onClick={onSkip}>
            Skip for now
          </button>
          <button
            type="button"
            className="modal-btn modal-confirm"
            onClick={onConfirm}
            autoFocus
          >
            Upload now
          </button>
        </div>
      </div>
    </div>
  );
}

function MigrationProgressPill({ done, total }) {
  return (
    <div className="migration-pill" role="status" aria-live="polite">
      Uploading media — {done} of {total}
    </div>
  );
}

// --- Auth modals ---

// Per-person invite & access modal.
//
// Phase 2 model: each person card carries `auth = { email, role, scope }`.
// Setting these fields on a person IS the act of inviting them — once the
// admin clicks Send Invite, a Supabase magic link is dispatched, and on
// click the AuthContext finds this email on the tree and provisions the
// matching profile (with role/scope) automatically.
function ManageAccessModal({ person, onSave, onRemove, onSendInvite, onClose }) {
  const existing = person.auth || {};
  const [email, setEmail] = useState(existing.email || '');
  const [role, setRole] = useState(
    existing.role === 'admin' || existing.role === 'editor' ? existing.role : 'viewer'
  );
  const [scope, setScope] = useState(
    existing.scope === 'branch' || existing.scope === 'tree' || existing.scope === 'global'
      ? existing.scope
      : 'tree'
  );
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sentMsg, setSentMsg] = useState('');

  const validEmail = (s) =>
    typeof s === 'string' && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s.trim());

  const buildAuthData = () => ({
    email: email.trim(),
    role,
    // scope is only meaningful for editors; admins are global, viewers
    // can't edit at all.
    scope: role === 'editor' ? scope : null,
    // Stamp only on first invite — subsequent saves preserve the original.
    invitedAt: existing.invitedAt || new Date().toISOString(),
  });

  const handleSave = (e) => {
    e.preventDefault();
    if (!validEmail(email)) {
      setError('Please enter a valid email.');
      return;
    }
    onSave(buildAuthData());
    // Close on explicit Save so the click has visible feedback. Send Invite
    // also calls onSave but stays open to show the green "Invite sent" text.
    onClose();
  };

  const handleSendInvite = async () => {
    if (!validEmail(email)) {
      setError('Please enter a valid email before sending an invite.');
      return;
    }
    setBusy(true);
    setError('');
    setSentMsg('');
    // Save the auth data first so the invited user's email is on the tree
    // before the magic link is delivered — otherwise their first sign-in
    // wouldn't find a matching person and they'd be marked not-invited.
    const authData = buildAuthData();
    onSave(authData);
    const result = await onSendInvite(authData.email);
    setBusy(false);
    if (result?.error) {
      setError(result.error);
    } else {
      setSentMsg(`Invite sent to ${authData.email}.`);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3 className="modal-title">
          Invite & access — {person.name?.trim() || 'unnamed'}
        </h3>
        <form onSubmit={handleSave} className="auth-form">
          <label className="auth-field">
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); setSentMsg(''); }}
              placeholder="aunt.mary@example.com"
              autoFocus
              required
            />
          </label>

          <div className="auth-field">
            <span>Role</span>
            <div className="auth-perm-toggle" role="radiogroup">
              {[
                { v: 'viewer', label: 'Viewer' },
                { v: 'editor', label: 'Editor' },
                { v: 'admin', label: 'Admin' },
              ].map((r) => (
                <button
                  key={r.v}
                  type="button"
                  role="radio"
                  aria-checked={role === r.v}
                  className={`auth-perm-btn${role === r.v ? ' active' : ''}`}
                  onClick={() => setRole(r.v)}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {role === 'editor' && (
            <div className="auth-field">
              <span>Edit scope</span>
              <div className="auth-perm-toggle" role="radiogroup">
                {[
                  { v: 'branch', label: 'Own branch', hint: '±2 generations' },
                  { v: 'tree', label: 'This tree', hint: 'no portal trees' },
                  { v: 'global', label: 'Everything', hint: 'all trees' },
                ].map((s) => (
                  <button
                    key={s.v}
                    type="button"
                    role="radio"
                    aria-checked={scope === s.v}
                    className={`auth-perm-btn${scope === s.v ? ' active' : ''}`}
                    onClick={() => setScope(s.v)}
                    title={s.hint}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <p className="auth-hint">
            {existing.email
              ? 'Update fields and Save, or Send Invite to email a fresh sign-in link.'
              : 'Send Invite emails a magic link. They sign in with the link — no password needed.'}
          </p>

          {error && <p className="auth-error">{error}</p>}
          {sentMsg && <p className="login-msg" style={{ marginTop: 0 }}>{sentMsg}</p>}

          <div className="modal-actions modal-actions-three">
            <button type="button" className="modal-btn modal-cancel" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            {existing.email && (
              <button type="button" className="modal-btn modal-discard" onClick={onRemove} disabled={busy}>
                Remove
              </button>
            )}
            <button
              type="button"
              className="modal-btn modal-discard"
              onClick={handleSendInvite}
              disabled={busy}
              title="Save and email a magic-link sign-in to this person"
            >
              {busy ? 'Sending…' : 'Send Invite'}
            </button>
            <button type="submit" className="modal-btn modal-save" disabled={busy}>
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MemorialOverlay({ person, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const name = person.name?.trim() || 'In Loving Memory';
  const birth = (person.birth || '').trim();
  const death = (person.death || '').trim();

  return (
    <div className="memorial-backdrop" onClick={onClose}>
      <div className="memorial-stage" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="memorial-close"
          onClick={onClose}
          aria-label="Close memorial"
        >
          <CloseSvg size={20} />
        </button>
        <div className="memorial-frame-outer">
          <div className="memorial-frame-mid">
            <div className="memorial-frame-inner">
              <div className="memorial-photo-wrap">
                {person.photo ? (
                  <MediaImg src={person.photo} alt={name} className="memorial-photo" />
                ) : (
                  <div className="memorial-photo memorial-photo-placeholder" aria-hidden="true">
                    <UserSvg size={120} />
                  </div>
                )}
              </div>
              <h2 className="memorial-name">{name}</h2>
              {(birth || death) && (
                <p className="memorial-dates">
                  {birth}
                  {birth && death && <span className="memorial-dash"> — </span>}
                  {death}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="memorial-altar-row">
          <div className="memorial-candle memorial-candle-left">
            <CandleSvg />
          </div>
          <div className="memorial-incense-altar">
            <MemorialIncenseSvg />
          </div>
          <div className="memorial-candle memorial-candle-right">
            <CandleSvg />
          </div>
        </div>
      </div>
    </div>
  );
}

function BackupModal({ onClose, localMediaCount = 0, onSyncMedia }) {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [messageKind, setMessageKind] = useState('info'); // 'info' | 'error'

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const lastBackup = localStorage.getItem(LAST_BACKUP_KEY);
  const lastBackupDisplay = lastBackup
    ? new Date(lastBackup).toLocaleString()
    : 'never';

  const doExport = async () => {
    try {
      const count = await exportAllGiaPha();
      appendAdminLog({
        actor: 'admin',
        actorName: 'Admin',
        action: 'export_backup',
        target: null,
        details: { count },
      });
      setMessageKind('info');
      setMessage(`Backup downloaded — ${count} entries saved.`);
    } catch (e) {
      setMessageKind('error');
      setMessage('Export failed: ' + (e.message || e));
    }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(
      'Restoring will overwrite the trees, portals, stories, photos, videos, ' +
      'logins, and log currently in this browser. Continue?'
    )) {
      e.target.value = '';
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const count = await importGiaPhaBackup(file);
      appendAdminLog({
        actor: 'admin',
        actorName: 'Admin',
        action: 'restore_backup',
        target: null,
        details: { count, file: file.name },
      });
      setMessageKind('info');
      setMessage(`Restored ${count} entries. Reloading…`);
      setTimeout(() => window.location.reload(), 700);
    } catch (err) {
      setMessageKind('error');
      setMessage('Import failed: ' + (err.message || 'invalid file'));
    } finally {
      setBusy(false);
      if (e.target) e.target.value = '';
    }
  };

  const doSyncMedia = async () => {
    if (!onSyncMedia || localMediaCount <= 0) return;
    setBusy(true);
    setMessage('');
    try {
      await onSyncMedia();
      setMessageKind('info');
      setMessage('Local media sync finished. Any files that uploaded successfully are now available in other browsers.');
    } catch (e) {
      setMessageKind('error');
      setMessage('Media sync failed: ' + (e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3 className="modal-title">Backup & Restore</h3>
        <p className="modal-message">
          Save a copy of every tree, portal, story, photo, video, login, and admin
          log stored in this browser to a JSON file. Keep it somewhere safe — cloud
          drive, USB stick, email to yourself. Restore it on this or any browser to
          bring it all back.
        </p>
        <p className="auth-hint">
          Last backup: <code>{lastBackupDisplay}</code>
          {isBackupStale() && lastBackup && (
            <>
              <br />
              <span style={{ color: 'var(--red-bright)' }}>
                It's been more than {BACKUP_STALE_DAYS} days — consider downloading a fresh one.
              </span>
            </>
          )}
        </p>
        {message && (
          <p
            className={messageKind === 'error' ? 'auth-error' : 'auth-hint'}
            style={messageKind === 'info' ? { color: 'var(--gold-bright)' } : null}
          >
            {message}
          </p>
        )}
        <div className="modal-actions modal-actions-three">
          <button type="button" className="modal-btn modal-cancel" onClick={onClose}>
            Close
          </button>
          {localMediaCount > 0 && (
            <button
              type="button"
              className="modal-btn"
              onClick={doSyncMedia}
              disabled={busy || !onSyncMedia}
            >
              Sync {localMediaCount} Local Media
            </button>
          )}
          <button
            type="button"
            className="modal-btn modal-discard"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            {busy ? 'Restoring…' : 'Restore from File'}
          </button>
          <button type="button" className="modal-btn modal-save" onClick={doExport}>
            Download Backup
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          style={{ display: 'none' }}
          onChange={doImport}
        />
      </div>
    </div>
  );
}

function AdminLogModal({ onClose }) {
  const [entries, setEntries] = useState(() => loadAdminLog());

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const formatTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch (e) {
      return iso;
    }
  };

  const labelByAction = {
    login: 'Logged in',
    logout: 'Logged out',
    grant_access: 'Granted access',
    update_access: 'Updated access',
    remove_access: 'Removed access',
    send_invite: 'Sent invite',
    upload_photo: 'Added photo',
    upload_audio: 'Added audio',
    upload_video: 'Added video',
    add_youtube: 'Added YouTube link',
    save_story: 'Saved story',
    add_marriage: 'Added marriage',
    add_child: 'Added child',
    add_parents: 'Added parents',
    delete_person: 'Deleted person',
    delete_marriage: 'Deleted marriage',
    replace_spouse: 'Replaced spouse',
    export_backup: 'Exported backup',
    restore_backup: 'Restored backup',
    mark_marriage_ended: 'Marked marriage ended',
    mark_marriage_active: 'Marked marriage active',
  };

  // Compose a small string describing the impact / context details a given
  // entry carries — kept short and bulleted so the list stays scannable.
  const describeDetails = (entry) => {
    const out = [];
    const d = entry.details || {};
    if (d.email) out.push(d.email);
    if (d.role) out.push(`role: ${d.role}`);
    if (d.scope) out.push(`scope: ${d.scope}`);
    if (d.from && d.to) out.push(`${d.from} → ${d.to}`);
    if (d.year) out.push(`year: ${d.year}`);
    if (typeof d.count === 'number') {
      out.push(`${d.count} item${d.count === 1 ? '' : 's'}`);
    }
    const removed = [];
    if (d.people) removed.push(`${d.people} ${d.people === 1 ? 'person' : 'people'}`);
    if (d.photos) removed.push(`${d.photos} photo${d.photos === 1 ? '' : 's'}`);
    if (d.audio) removed.push(`${d.audio} audio`);
    if (d.videos) removed.push(`${d.videos} video${d.videos === 1 ? '' : 's'}`);
    if (d.stories) removed.push(`${d.stories} story page${d.stories === 1 ? '' : 's'}`);
    if (removed.length) out.push(removed.join(', '));
    if (d.url) out.push(d.url);
    if (d.gender) out.push(d.gender);
    if (d.tree) out.push(`(${d.tree})`);
    return out.join(' · ');
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-wide log-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="log-header">
          <h3 className="modal-title" style={{ margin: 0 }}>Admin Log</h3>
          <button
            type="button"
            className="auth-link-btn"
            onClick={() => {
              if (window.confirm('Clear the entire log?')) {
                clearAdminLog();
                setEntries([]);
              }
            }}
          >
            Clear
          </button>
        </div>
        {entries.length === 0 ? (
          <p className="empty-note">No log entries yet.</p>
        ) : (
          <ul className="log-list">
            {entries.map((e, i) => {
              const details = describeDetails(e);
              return (
                <li key={i} className="log-entry">
                  <div className="log-time">{formatTime(e.timestamp)}</div>
                  <div className="log-actor">{e.actorName || e.actor}</div>
                  <div className="log-action">
                    {labelByAction[e.action] || e.action}
                    {e.target?.name ? <span className="log-target"> · {e.target.name}</span> : null}
                    {details ? <span className="log-detail"> · {details}</span> : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <div className="modal-actions">
          <button type="button" className="modal-btn modal-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// --- Mode toggle ---

function ModeToggle({ mode, onChange }) {
  const isEdit = mode === 'edit';
  return (
    <button
      type="button"
      className={`mode-toggle mode-toggle-${mode}`}
      onClick={() => onChange(isEdit ? 'view' : 'edit')}
      aria-label={isEdit ? 'Switch to view mode' : 'Switch to edit mode'}
      title={isEdit ? 'Currently editing — click to view' : 'Currently viewing — click to edit'}
    >
      <span className="mode-toggle-icon mode-toggle-eye"><EyeSvg size={13} /></span>
      <span className="mode-toggle-icon mode-toggle-pencil"><PencilSvg size={13} /></span>
      <span className="mode-toggle-thumb" />
    </button>
  );
}

// --- App ---

function App() {
  return (
    <AuthProvider>
      <AppGate />
    </AuthProvider>
  );
}

function AppGate() {
  const {
    user,
    profile,
    loading,
    notInvited,
    profileError,
    recoveryMode,
    clearRecoveryMode,
    signOut,
  } = useAuth();
  const [route, setRoute] = useState(parseHash);

  useEffect(() => {
    const onHash = () => setRoute(parseHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Dev-only boot diagnostic — confirms env vars reached the bundle. Gated
  // so prod consoles don't leak whether Cloudinary/R2/Supabase are wired.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    console.log(
      '[gia-pha] cloud config —',
      'cloudinary:', cloudinaryConfigured,
      '| r2:', r2Configured,
      '| supabase:', !!process.env.REACT_APP_SUPABASE_URL && !!process.env.REACT_APP_SUPABASE_ANON_KEY
    );
  }, []);

  // While the persisted Supabase session is resolving, render a blank gate
  // rather than flashing the LoginPage.
  if (loading) return <div className="auth-loading" />;
  if (recoveryMode) {
    return (
      <SetPasswordPage
        heading="New password"
        subtitle="Set a password to finish signing in"
        onDone={clearRecoveryMode}
      />
    );
  }
  if (notInvited) return <NotInvitedPage />;
  if (!user) return <LoginPage />;

  // Profile materialization happens asynchronously after SIGNED_IN, so
  // there's a brief window where user is truthy but profile hasn't landed
  // yet. Show the blank gate during that window — only fall through to
  // the "Access setup failed" screen when ensure_profile actually errored
  // (profileError is populated). Without this gate the user saw the error
  // flash for ~100ms on every login.
  if (!profile && !profileError) return <div className="auth-loading" />;
  if (!profile) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h1>Gia Phả</h1>
          <p className="login-tagline">Access setup failed</p>
          <p className="login-error" style={{ marginTop: 16 }}>
            Couldn't materialize your profile: <code>{profileError}</code>.
            {' '}Contact the family administrator.
          </p>
          <button
            type="button"
            className="login-magic-btn"
            style={{ marginTop: 18 }}
            onClick={signOut}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  if (route.kind === 'portal') {
    return (
      <TreeApp
        key={`portal-${route.portalId}`}
        mode="portal"
        portalId={route.portalId}
      />
    );
  }
  return <TreeApp key="main" mode="main" />;
}

// --- TreeApp ---

function TreeApp({ mode: routeMode, portalId }) {
  const isPortal = routeMode === 'portal';
  const storageKey = isPortal ? PORTAL_TREE_PREFIX + portalId : MAIN_STORAGE_KEY;
  const currentTreeKey = isPortal ? `portal/${portalId}` : 'main';

  const { user: authUser, profile: authProfile, signOut: authSignOut } = useAuth();
  const isAuthAdmin = authProfile?.role === 'admin';

  const [tree, setTree] = useState(() =>
    isPortal ? loadPortalTree(portalId) : loadMainTree()
  );
  const [me, setMeState] = useState(loadGlobalMe);
  const meId = me.id;
  const [confirmState, setConfirmState] = useState(null);
  const [mediaModal, setMediaModal] = useState(null);
  const [viewModeRaw, setViewMode] = useState(() => {
    const saved = localStorage.getItem(MODE_STORAGE_KEY);
    return saved === 'edit' ? 'edit' : 'view';
  });
  // isAdmin and canEdit are driven by the Supabase profile role assigned
  // server-side via the ensure_profile RPC. Scope enforcement (branch/
  // tree/global) is Phase 2B — for now editor = full edit.
  const isAdmin = isAuthAdmin;
  const canEdit = isAdmin || authProfile?.role === 'editor';
  // Non-edit users are forced to view mode regardless of the saved preference.
  const viewMode = canEdit ? viewModeRaw : 'view';
  const [storageError, setStorageError] = useState(null);
  const [authPanel, setAuthPanel] = useState(null); // { personId } | null
  const [logPanel, setLogPanel] = useState(false);
  const [backupPanel, setBackupPanel] = useState(false);
  const [memorialPersonId, setMemorialPersonId] = useState(null);
  const [cloudConflict, setCloudConflict] = useState(null); // { remoteData, remoteUpdatedAt } | null
  const [cloudPushReadyKey, setCloudPushReadyKey] = useState(null);
  const [migrationPrompt, setMigrationPrompt] = useState(null); // { count } | null
  const [migrationProgress, setMigrationProgress] = useState(null); // { done, total } | null
  // Hard-stop confirmation for any destructive prune. The simple confirmState
  // above is used for low-risk single-person deletes; this drives the typed-
  // DELETE flow whenever the impact touches >1 person or any media/story.
  const [deleteImpactState, setDeleteImpactState] = useState(null);
  // Replace-or-remove choice when the spouse-card trash is clicked.
  const [spouseActionState, setSpouseActionState] = useState(null);
  // 30-second in-memory undo buffer for the most recent destructive action.
  // Holds the previous tree snapshot AND the IDB blob refs that the delete
  // *would* have removed — we defer the actual blob delete until the window
  // expires so undo can fully restore.
  const [undoState, setUndoState] = useState(null); // { snapshot, label, expiresAt, idbRefs }
  const undoTimerRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(tree));
    } catch (e) {
      setStorageError(
        'Storage limit reached — recent media may be too large to save. Try smaller files.'
      );
    }
  }, [tree, storageKey]);

  // Cloud push — debounced 2s after the latest edit. The local state +
  // localStorage stay the source of truth; this only mirrors changes.
  // Guard on authProfile: don't push until the session is fully established
  // and the profile RPC has confirmed the user is authenticated. Pushing
  // before that causes a 401 / RLS violation on the trees table.
  useEffect(() => {
    if (!cloudSyncConfigured) return;
    if (!authProfile) return;
    if (cloudPushReadyKey !== storageKey) return;
    cloudSchedulePush(storageKey, tree);
  }, [tree, storageKey, authProfile, cloudPushReadyKey]);

  // Boot pull — once per mount per storageKey. If the remote has been
  // modified since our last successful sync AND the data differs from
  // local, surface a conflict prompt. Until this finishes, do not let the
  // initial local tree push back to Supabase; otherwise an old browser can
  // overwrite the newer cloud row while the conflict dialog is still open.
  useEffect(() => {
    if (!cloudSyncConfigured) return;
    let cancelled = false;
    setCloudPushReadyKey(null);
    (async () => {
      const remote = await cloudPullTree(storageKey);
      if (cancelled) return;
      if (!remote) {
        setCloudPushReadyKey(storageKey);
        return;
      }
      const remoteTs = new Date(remote.updatedAt).getTime();
      if (remoteTs <= cloudGetLastSyncTs(storageKey)) {
        setCloudPushReadyKey(storageKey);
        return;
      }
      // Remote is newer than our last sync — compare against the local tree
      // we currently have on screen.
      const localStr = JSON.stringify(treeRef.current);
      const remoteStr = JSON.stringify(remote.data);
      if (localStr === remoteStr) {
        // Same content; just refresh the sync timestamp.
        cloudSetLastSyncTs(storageKey, remote.updatedAt);
        setCloudPushReadyKey(storageKey);
        return;
      }
      setCloudConflict({
        remoteData: remote.data,
        remoteUpdatedAt: remote.updatedAt,
      });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // One-shot migration prompt — counts media still living in this browser's
  // IndexedDB. Those `idb:` refs can be stored in the cloud tree JSON, but
  // another browser cannot render them, so offer to upload them to the real
  // media clouds when at least one relevant service is configured.
  useEffect(() => {
    if (!cloudinaryConfigured && !r2Configured) return;
    const count = countIdbRefs(tree);
    if (count <= 0) {
      setMigrationPrompt(null);
      return;
    }
    const skipKey = 'gia-pha-migration-skipped-' + storageKey;
    const skippedCount = Number(
      sessionStorage.getItem(skipKey) || localStorage.getItem(skipKey) || 0
    );
    if (count > skippedCount) setMigrationPrompt({ count });
  }, [storageKey, tree]);

  // Admin log cloud mirror. Logs are not part of any single tree, so merge the
  // remote and local arrays on sign-in and then mirror new local entries.
  useEffect(() => {
    if (!cloudSyncConfigured) return;
    if (!authProfile) return;
    let cancelled = false;
    (async () => {
      const remote = await cloudPullTree(ADMIN_LOG_KEY);
      if (cancelled) return;
      const localLog = loadAdminLog();
      if (!remote) {
        if (localLog.length) cloudSchedulePush(ADMIN_LOG_KEY, localLog);
        return;
      }
      const merged = mergeAdminLogs(localLog, remote.data);
      const localStr = JSON.stringify(localLog);
      const remoteStr = JSON.stringify(Array.isArray(remote.data) ? remote.data : []);
      const mergedStr = JSON.stringify(merged);
      if (mergedStr !== localStr) saveAdminLog(merged, { push: false });
      cloudSetLastSyncTs(ADMIN_LOG_KEY, remote.updatedAt);
      if (mergedStr !== remoteStr) cloudSchedulePush(ADMIN_LOG_KEY, merged);
    })();
    return () => { cancelled = true; };
  }, [authProfile]);

  // One-shot migration: if the loaded tree still has any data: URLs embedded
  // (legacy from before media moved to IndexedDB), move them to IDB and
  // rewrite the tree with idb: refs in their place. Idempotent across reloads.
  useEffect(() => {
    if (!treeHasDataUrls(tree)) return;
    let cancelled = false;
    (async () => {
      const { tree: migrated, changed } = await migrateTreeMediaToIdb(tree);
      if (cancelled || !changed) return;
      setTree(migrated);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(() => {
    saveGlobalMe(me);
  }, [me]);

  // Resolve a human-readable name for the current actor from the Supabase
  // profile: prefer the tree person their profile is linked to (set on
  // invite), fall back to the email, then a generic label. Defined early
  // so every mutation handler below can call logEvent uniformly.
  const logEvent = useCallback(
    (action, target, details) => {
      const t = treeRef.current;
      const linkedPersonId = authProfile?.person_id;
      const linkedPerson = linkedPersonId ? findPerson(t, linkedPersonId) : null;
      const actorName =
        linkedPerson?.name?.trim() ||
        authProfile?.email ||
        authUser?.email ||
        (authProfile?.role === 'admin' ? 'Admin' : 'unknown');
      const actorId = authUser?.id || linkedPersonId || 'admin';
      appendAdminLog({
        actor: actorId,
        actorName,
        action,
        target: target || null,
        details: { tree: currentTreeKey, ...(details || {}) },
      });
    },
    [authProfile, authUser, currentTreeKey]
  );

  useEffect(() => {
    if (canEdit) localStorage.setItem(MODE_STORAGE_KEY, viewModeRaw);
  }, [viewModeRaw, canEdit]);

  // If Me's home tree IS this tree but the person was deleted, clear Me.
  // (Cross-tree deletions are out of scope here — Me is preserved.)
  useEffect(() => {
    if (me.id && me.tree === currentTreeKey && !findPerson(tree, me.id)) {
      setMeState({ id: null, tree: null });
    }
  }, [tree, me, currentTreeKey]);

  const treeRef = useRef(tree);
  useEffect(() => { treeRef.current = tree; }, [tree]);

  const updatePerson = useCallback((personId, updater) => {
    const found = findPerson(treeRef.current, personId);
    if (!found) return;
    const next = updater(found);
    syncPersonAcrossTrees(personId, storageKey, pickSyncedPersonFields(next));
    setTree((t) => mapPersons(t, (p) => (p.id === personId ? next : p)));
  }, [storageKey]);

  const updateMarriage = useCallback((marriageId, updater) => {
    const found = findMarriage(treeRef.current, marriageId);
    if (!found) return;
    const next = updater(found);
    syncMarriageAcrossTrees(marriageId, storageKey, pickSyncedMarriageFields(next));
    setTree((t) => mapMarriages(t, (m) => (m.id === marriageId ? next : m)));
  }, [storageKey]);

  const setMarriageMedia = useCallback((marriageId, mediaUpdater) => {
    const found = findMarriage(treeRef.current, marriageId);
    if (!found) return;
    const nextMedia = mediaUpdater(ensureMedia(found.media));
    syncMarriageAcrossTrees(marriageId, storageKey, { media: nextMedia });
    setTree((t) =>
      mapMarriages(t, (m) =>
        m.id === marriageId ? { ...m, media: nextMedia } : m
      )
    );
  }, [storageKey]);

  const toggleMarriageEnded = useCallback(
    (marriageId) => {
      const t = treeRef.current;
      const m = findMarriage(t, marriageId);
      if (!m) return;
      const newEnded = !m.ended;
      syncMarriageAcrossTrees(marriageId, storageKey, { ended: newEnded });
      setTree((cur) =>
        mapMarriages(cur, (mm) =>
          mm.id === marriageId ? { ...mm, ended: newEnded } : mm
        )
      );
      logEvent(
        newEnded ? 'mark_marriage_ended' : 'mark_marriage_active',
        { kind: 'marriage', id: marriageId, name: marriageDisplayName(t, marriageId) },
        { year: m.marriageYear || '' }
      );
    },
    [storageKey, logEvent]
  );

  const setPersonMedia = useCallback((personId, mediaUpdater) => {
    const found = findPerson(treeRef.current, personId);
    if (!found) return;
    const nextMedia = mediaUpdater(ensureMedia(found.media));
    syncPersonAcrossTrees(personId, storageKey, { media: nextMedia });
    setTree((t) =>
      mapPersons(t, (p) =>
        p.id === personId ? { ...p, media: nextMedia } : p
      )
    );
  }, [storageKey]);

  const addMarriage = useCallback((personId) => {
    const t = treeRef.current;
    const p = findPerson(t, personId);
    setTree((cur) =>
      mapPersons(cur, (pp) =>
        pp.id === personId ? { ...pp, marriages: [...pp.marriages, newMarriage()] } : pp
      )
    );
    logEvent(
      'add_marriage',
      { kind: 'person', id: personId, name: p?.name?.trim() || 'unnamed' },
      {}
    );
  }, [logEvent]);

  const addChild = useCallback((marriageId, anchorChildId, where, gender) => {
    const t = treeRef.current;
    setTree((cur) =>
      mapMarriages(cur, (m) => {
        if (m.id !== marriageId) return m;
        const newKid = newPerson(gender ? { gender } : {});
        let children;
        if (anchorChildId && (where === 'before' || where === 'after')) {
          const idx = m.children.findIndex((c) => c.id === anchorChildId);
          if (idx === -1) {
            children = [...m.children, newKid];
          } else {
            const insertAt = where === 'before' ? idx : idx + 1;
            children = [
              ...m.children.slice(0, insertAt),
              newKid,
              ...m.children.slice(insertAt),
            ];
          }
        } else {
          children = [...m.children, newKid];
        }
        return { ...m, children };
      })
    );
    logEvent(
      'add_child',
      { kind: 'marriage', id: marriageId, name: marriageDisplayName(t, marriageId) },
      gender ? { gender } : {}
    );
  }, [logEvent]);

  const addParents = useCallback(() => {
    setTree((current) => {
      const newRoot = newPerson();
      const marriage = newMarriage();
      marriage.children = [current];
      newRoot.marriages = [marriage];
      return newRoot;
    });
    logEvent('add_parents', null, {});
  }, [logEvent]);

  // Commit the IDB blobs that a previous delete had staged for cleanup, then
  // forget the undo buffer. Called when (a) the 30s window expires, or (b) a
  // new destructive action arrives and we need to advance — letting the
  // earlier delete's blobs leak forever otherwise.
  const finalizeUndo = useCallback((snap) => {
    if (!snap) return;
    if (snap.idbRefs && snap.idbRefs.length) {
      idbDelete(snap.idbRefs).catch(() => {});
    }
  }, []);

  // Mirror undoState into a ref so the unmount cleanup can finalize any
  // pending IDB deletion without re-running on every state change.
  const undoStateRef = useRef(null);
  useEffect(() => { undoStateRef.current = undoState; }, [undoState]);

  // Stage a fresh undo buffer. Any previous buffer's pending IDB cleanup is
  // committed first so blobs don't accumulate when the user does several
  // destructive actions in a row.
  const stageUndo = useCallback(({ snapshot, label, idbRefs }) => {
    setUndoState((prev) => {
      if (prev) finalizeUndo(prev);
      return { snapshot, label, expiresAt: Date.now() + 30000, idbRefs };
    });
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      setUndoState((cur) => {
        if (cur) finalizeUndo(cur);
        return null;
      });
      undoTimerRef.current = null;
    }, 30000);
  }, [finalizeUndo]);

  const performUndo = useCallback(() => {
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
    setUndoState((cur) => {
      if (!cur) return null;
      // Restoring the snapshot abandons the staged IDB refs — the blobs they
      // pointed to were never actually removed, so the restored tree's
      // idb: links resolve correctly.
      setTree(cur.snapshot);
      return null;
    });
  }, []);

  // On unmount (route change, full reload), commit any pending IDB cleanup
  // for the most recent staged delete so orphaned blobs don't accumulate.
  // The user can no longer undo at that point — the tree state on disk is
  // already in the post-delete shape.
  useEffect(() => {
    return () => {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      const pending = undoStateRef.current;
      if (pending) finalizeUndo(pending);
    };
  }, [finalizeUndo]);

  // Run the actual prune for a person-removal. The impact preview gates this;
  // never call directly from a UI button.
  const performDeletePerson = useCallback((person) => {
    const personId = person.id;
    const snapshot = treeRef.current;
    const idbRefs = collectIdbRefsInPerson(person);
    const impact = impactOfPersonRemoval(person);
    setTree((cur) => removePersonFromTree(cur, personId));
    stageUndo({
      snapshot,
      label: `Removed ${person.name?.trim() || 'person'}`,
      idbRefs,
    });
    logEvent(
      'delete_person',
      { kind: 'person', id: personId, name: person.name?.trim() || 'unnamed' },
      {
        people: impact.people,
        photos: impact.photos,
        audio: impact.audio,
        videos: impact.videos,
        stories: impact.stories,
      }
    );
  }, [stageUndo, logEvent]);

  const performDeleteMarriage = useCallback((marriage) => {
    const marriageId = marriage.id;
    const snapshot = treeRef.current;
    const idbRefs = collectIdbRefsInMarriage(marriage);
    const impact = impactOfMarriageRemoval(marriage);
    const label = marriageDisplayName(treeRef.current, marriageId);
    setTree((cur) => removeMarriageFromTree(cur, marriageId));
    stageUndo({
      snapshot,
      label: `Removed marriage to ${marriage.spouse?.name?.trim() || 'spouse'}`,
      idbRefs,
    });
    logEvent(
      'delete_marriage',
      { kind: 'marriage', id: marriageId, name: label },
      {
        people: impact.people,
        photos: impact.photos,
        audio: impact.audio,
        videos: impact.videos,
        stories: impact.stories,
      }
    );
  }, [stageUndo, logEvent]);

  const deletePerson = useCallback((personId) => {
    const t = treeRef.current;
    if (t.id === personId) return;
    const person = findPerson(t, personId);
    if (!person) return;
    const label = person.name?.trim() || 'this person';
    const impact = impactOfPersonRemoval(person);

    if (!impactNeedsTypedConfirm(impact)) {
      // Single empty leaf — keep the lightweight confirm.
      setConfirmState({
        message: `Delete ${label}?`,
        onConfirm: () => {
          performDeletePerson(person);
          setConfirmState(null);
        },
      });
      return;
    }

    setDeleteImpactState({
      title: `Deleting ${label} will permanently remove:`,
      impact,
      onConfirm: () => {
        performDeletePerson(person);
        setDeleteImpactState(null);
      },
    });
  }, [performDeletePerson]);

  const deleteMarriage = useCallback((marriageId) => {
    const t = treeRef.current;
    const marriage = findMarriage(t, marriageId);
    if (!marriage) return;
    const spouseLabel = marriage.spouse?.name?.trim() || 'spouse';
    const impact = impactOfMarriageRemoval(marriage);

    if (!impactNeedsTypedConfirm(impact)) {
      setConfirmState({
        message: `Delete this marriage to ${spouseLabel}?`,
        onConfirm: () => {
          performDeleteMarriage(marriage);
          setConfirmState(null);
        },
      });
      return;
    }

    setDeleteImpactState({
      title: `Deleting this marriage to ${spouseLabel} will permanently remove:`,
      impact,
      onConfirm: () => {
        performDeleteMarriage(marriage);
        setDeleteImpactState(null);
      },
    });
  }, [performDeleteMarriage]);

  // Spouse-card trash button — opens the Replace / Edit / Remove choice
  // instead of going straight to a destructive prune. Replace preserves the
  // children below by swapping only the spouse object on the marriage.
  const handleSpouseTrash = useCallback((marriageId) => {
    const t = treeRef.current;
    const marriage = findMarriage(t, marriageId);
    if (!marriage) return;
    setSpouseActionState({
      marriageId,
      spouseName: marriage.spouse?.name || '',
    });
  }, []);

  const replaceSpouse = useCallback((marriageId) => {
    const t = treeRef.current;
    const marriage = findMarriage(t, marriageId);
    if (!marriage) return;
    const oldName = marriage.spouse?.name?.trim() || '';
    const promptMsg = oldName
      ? `Replace ${oldName} with whom? Enter the correct name:`
      : 'Enter the correct name for this person:';
    const newName = window.prompt(promptMsg, '');
    if (newName == null) return; // user cancelled
    const trimmed = newName.trim();
    if (!trimmed) return;
    // Snapshot for undo — replacing a spouse drops their personal media,
    // story, and any sub-marriages they had (their children stay because
    // children live on the marriage, not on the spouse). Capture IDB refs
    // from the OLD spouse only.
    const snapshot = treeRef.current;
    const idbRefs = collectIdbRefsInPerson(marriage.spouse);
    const newSpouse = newPerson({ name: trimmed });
    setTree((cur) => replaceSpouseInTree(cur, marriageId, newSpouse));
    stageUndo({
      snapshot,
      label: `Replaced ${oldName || 'spouse'} with ${trimmed}`,
      idbRefs,
    });
    logEvent(
      'replace_spouse',
      { kind: 'marriage', id: marriageId, name: marriageDisplayName(snapshot, marriageId) },
      { from: oldName || 'unnamed', to: trimmed }
    );
    setSpouseActionState(null);
  }, [stageUndo, logEvent]);

  const setMe = useCallback((personId) => {
    setMeState((current) => {
      if (current.id === personId) return { id: null, tree: null };
      return { id: personId, tree: currentTreeKey };
    });
  }, [currentTreeKey]);

  const openPortal = useCallback(
    (person, context) => {
      let updated = treeRef.current;
      if (!person.isPortal) {
        updated = mapPersons(updated, (p) =>
          p.id === person.id ? { ...p, isPortal: true } : p
        );
        setTree(updated);
        try {
          localStorage.setItem(storageKey, JSON.stringify(updated));
        } catch (e) {}
      }
      const fresh = findPerson(updated, person.id) || person;

      // Capture every field that describes the person (the same set that
      // syncs across trees). Without this, a freshly-created portal tree
      // started blank for gender / media / auth even when the source had
      // them. The seed is consumed by loadPortalTree on first visit; once
      // the portal tree is persisted, syncPersonAcrossTrees keeps it
      // current going forward.
      const seed = pickSyncedPersonFields(fresh);

      // Preserve the source-tree marriage on the way into the new portal:
      // the spouse becomes the new tree's root, the lineage parent flips to
      // become THEIR spouse, and the existing children come along with their
      // ids intact — so future leaf clicks find the same portals.
      if (context?.marriage && context?.lineageParent) {
        const m = context.marriage;
        const lp = context.lineageParent;
        seed.reverseMarriage = {
          marriageYear: m.marriageYear || '',
          ended: !!m.ended,
          media: m.media || ensureMedia(),
          partner: pickSyncedPersonFields(lp),
          partnerId: lp.id,
          children: (m.children || []).map((c) => ({
            id: c.id,
            ...pickSyncedPersonFields(c),
          })),
        };
      }

      try {
        localStorage.setItem(
          PORTAL_SEED_PREFIX + person.id,
          JSON.stringify(seed)
        );
      } catch (e) {}
      navigateToPortal(person.id);
    },
    [storageKey]
  );

  const focusMeRef = useRef(null);

  const runMigration = useCallback(async () => {
    setMigrationPrompt(null);
    setMigrationProgress({ done: 0, total: countIdbRefs(treeRef.current) });
    try {
      const { tree: migrated, succeeded, failed } = await migrateTreeIdbToCloud(
        treeRef.current,
        { onProgress: (p) => setMigrationProgress(p) }
      );
      setTree(migrated);
      // Record whatever idb refs remain (failed uploads, blob_missing orphans
      // from other devices) as the new skip threshold. Without this the
      // [storageKey, tree] effect re-fires the prompt the moment setTree
      // lands, since count is still > 0 and skippedCount is still 0.
      const remaining = countIdbRefs(migrated);
      const skipKey = 'gia-pha-migration-skipped-' + storageKey;
      localStorage.setItem(skipKey, String(remaining));
      sessionStorage.setItem(skipKey, String(remaining));
      console.log(
        `[gia-pha] migration complete — ${succeeded} uploaded, ${failed} failed.`
      );
    } catch (e) {
      console.error('[gia-pha] migration error:', e);
    } finally {
      setMigrationProgress(null);
    }
  }, [storageKey]);

  const skipMigration = useCallback(() => {
    const count = migrationPrompt?.count || countIdbRefs(treeRef.current);
    const skipKey = 'gia-pha-migration-skipped-' + storageKey;
    localStorage.setItem(skipKey, String(count));
    sessionStorage.setItem(skipKey, String(count));
    setMigrationPrompt(null);
  }, [storageKey, migrationPrompt]);

  const goHome = useCallback(() => {
    if (!me.id || !me.tree) return;
    // Already in Me's home tree → just animate to focus, no navigation.
    if (me.tree === currentTreeKey) {
      focusMeRef.current?.();
      return;
    }
    sessionStorage.setItem(HEART_FOCUS_FLAG, '1');
    navigateToTreeKey(me.tree);
  }, [me, currentTreeKey]);

  const openMarriageMedia = useCallback((marriageId, type) => {
    setMediaModal({ kind: 'marriage', id: marriageId, type });
  }, []);

  const openPersonMedia = useCallback((personId, type = 'photos') => {
    setMediaModal({ kind: 'person', id: personId, type });
  }, []);

  const openAuthPanel = useCallback((personId) => {
    setAuthPanel({ personId });
  }, []);

  const openMemorial = useCallback((personId) => {
    setMemorialPersonId(personId);
  }, []);

  const setPersonAuth = useCallback((personId, authData) => {
    setTree((t) =>
      mapPersons(t, (p) => (p.id === personId ? { ...p, auth: authData } : p))
    );
  }, []);

  const handlers = useMemo(
    () => ({
      updatePerson,
      updateMarriage,
      addMarriage,
      addChild,
      addParents,
      deletePerson,
      deleteMarriage,
      onSpouseTrash: handleSpouseTrash,
      setMe,
      openPortal,
      openMarriageMedia,
      openPersonMedia,
      openAuthPanel: isAdmin ? openAuthPanel : null,
      openMemorial,
      toggleMarriageEnded,
      isAdmin,
      meId,
      rootId: tree.id,
    }),
    [
      updatePerson,
      updateMarriage,
      addMarriage,
      addChild,
      addParents,
      deletePerson,
      deleteMarriage,
      handleSpouseTrash,
      setMe,
      openPortal,
      openMarriageMedia,
      openPersonMedia,
      openAuthPanel,
      openMemorial,
      toggleMarriageEnded,
      isAdmin,
      meId,
      tree.id,
    ]
  );

  const meName = useMemo(() => {
    if (!me.id) return '';
    if (me.tree === currentTreeKey) {
      return findPerson(tree, me.id)?.name?.trim() || '';
    }
    return lookupMeName(me);
  }, [me, tree, currentTreeKey]);

  // Back button: prefer "Back to {Me name}'s tree" routed to Me's home; on a
  // portal with no Me set, fall back to the original "back to family tree".
  const backInfo = useMemo(() => {
    if (me.id && me.tree && me.tree !== currentTreeKey) {
      return {
        label: `← Back to ${meName || 'family'}'s tree`,
        onClick: () => {
          sessionStorage.setItem(HEART_FOCUS_FLAG, '1');
          navigateToTreeKey(me.tree);
        },
      };
    }
    if (isPortal) {
      return { label: '← Back to family tree', onClick: navigateToMain };
    }
    return null;
  }, [me, meName, currentTreeKey, isPortal]);

  const portalSubject = isPortal ? findPerson(tree, portalId) : null;
  const subtitle = isPortal
    ? `Branch of ${portalSubject?.name?.trim() || 'a Family Member'}`
    : 'Family Tree';

  // Person object for the auth panel (admin only path).
  const authPanelPerson = authPanel?.personId
    ? findPerson(tree, authPanel.personId)
    : null;

  return (
    <ModeContext.Provider value={viewMode}>
     <LogContext.Provider value={logEvent}>
      <div className={`app mode-${viewMode}`}>
        <header className="app-header">
          {backInfo && (
            <button
              type="button"
              className="back-btn"
              onClick={backInfo.onClick}
              title={backInfo.label.replace(/^← /, '')}
            >
              {backInfo.label}
            </button>
          )}
          <div className="app-header-title">
            <h1>Gia Phả</h1>
            <p className="tagline">{subtitle}</p>
          </div>
          <div className="header-right">
            {authUser && (
              <div className="auth-account-pill" title={authUser.email}>
                <span className="auth-account-email">{authUser.email}</span>
                <button
                  type="button"
                  className="auth-signout-btn"
                  onClick={authSignOut}
                  title="Sign out of this account"
                >
                  Sign Out
                </button>
              </div>
            )}
            {isAdmin && (
              <button
                type="button"
                className={`auth-link-btn${isBackupStale() ? ' stale' : ''}`}
                onClick={() => setBackupPanel(true)}
                title={
                  isBackupStale()
                    ? 'Backup is stale — please download a fresh copy'
                    : 'Backup or restore data'
                }
              >
                Backup
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                className="auth-link-btn"
                onClick={() => setLogPanel(true)}
                title="View admin log"
              >
                Log
              </button>
            )}
            {/* Legacy in-tree per-person Login flow has been replaced by
                Supabase Auth — every user signs in through the auth-account
                pill above. The old LoginModal/FirstLoginModal/session-pill
                code is left in place but unreachable from the UI. */}
            {canEdit && <ModeToggle mode={viewMode} onChange={setViewMode} />}
          </div>
        </header>
        <TreeView
          tree={tree}
          meName={meName}
          onHeartClick={goHome}
          focusMeRef={focusMeRef}
          {...handlers}
        />
        {confirmState && (
          <ConfirmModal
            message={confirmState.message}
            onConfirm={confirmState.onConfirm}
            onCancel={() => setConfirmState(null)}
          />
        )}
        {deleteImpactState && (
          <DeleteImpactModal
            title={deleteImpactState.title}
            impact={deleteImpactState.impact}
            onConfirm={deleteImpactState.onConfirm}
            onCancel={() => setDeleteImpactState(null)}
          />
        )}
        {spouseActionState && (
          <SpouseActionModal
            spouseName={spouseActionState.spouseName}
            onReplace={() => replaceSpouse(spouseActionState.marriageId)}
            onEdit={() => setSpouseActionState(null)}
            onRemove={() => {
              const mid = spouseActionState.marriageId;
              setSpouseActionState(null);
              deleteMarriage(mid);
            }}
            onCancel={() => setSpouseActionState(null)}
          />
        )}
        {undoState && (
          <UndoToast
            label={undoState.label}
            expiresAt={undoState.expiresAt}
            onUndo={performUndo}
            onDismiss={() => {
              if (undoTimerRef.current) {
                clearTimeout(undoTimerRef.current);
                undoTimerRef.current = null;
              }
              setUndoState((cur) => {
                if (cur) finalizeUndo(cur);
                return null;
              });
            }}
          />
        )}
        {mediaModal && (
          <MediaModal
            kind={mediaModal.kind}
            id={mediaModal.id}
            initialType={mediaModal.type}
            tree={tree}
            setMarriageMedia={setMarriageMedia}
            setPersonMedia={setPersonMedia}
            onClose={() => setMediaModal(null)}
          />
        )}
        {storageError && (
          <div className="toast" onClick={() => setStorageError(null)}>
            {storageError}
            <span className="toast-dismiss">×</span>
          </div>
        )}
        {authPanel && authPanelPerson && (
          <ManageAccessModal
            person={authPanelPerson}
            onSave={(authData) => {
              setPersonAuth(authPanel.personId, authData);
              appendAdminLog({
                actor: 'admin',
                actorName: 'Admin',
                action: authPanelPerson.auth?.email ? 'update_access' : 'grant_access',
                target: { kind: 'person', id: authPanelPerson.id, name: authPanelPerson.name?.trim() || '' },
                details: { email: authData.email, role: authData.role, scope: authData.scope },
              });
            }}
            onRemove={() => {
              setPersonAuth(authPanel.personId, null);
              appendAdminLog({
                actor: 'admin',
                actorName: 'Admin',
                action: 'remove_access',
                target: { kind: 'person', id: authPanelPerson.id, name: authPanelPerson.name?.trim() || '' },
                details: {},
              });
              setAuthPanel(null);
            }}
            onSendInvite={async (email) => {
              // Fire a Supabase magic-link invite. The invited user's first
              // sign-in lands in AuthContext.ensureProfile, which finds them
              // on the tree by email and provisions their profile with the
              // role/scope already on the card.
              const { error } = await supabase.auth.signInWithOtp({
                email,
                options: {
                  emailRedirectTo:
                    window.location.origin + window.location.pathname,
                },
              });
              if (error) return { error: error.message };
              appendAdminLog({
                actor: 'admin',
                actorName: 'Admin',
                action: 'send_invite',
                target: { kind: 'person', id: authPanelPerson.id, name: authPanelPerson.name?.trim() || '' },
                details: { email },
              });
              return { ok: true };
            }}
            onClose={() => setAuthPanel(null)}
          />
        )}
        {logPanel && <AdminLogModal onClose={() => setLogPanel(false)} />}
        {backupPanel && (
          <BackupModal
            onClose={() => setBackupPanel(false)}
            localMediaCount={countIdbRefs(tree)}
            onSyncMedia={runMigration}
          />
        )}
        {memorialPersonId && (() => {
          const p = findPerson(tree, memorialPersonId);
          if (!p || !isDeceasedFromField(p.death)) return null;
          return (
            <MemorialOverlay
              person={p}
              onClose={() => setMemorialPersonId(null)}
            />
          );
        })()}
        {cloudConflict && (
          <CloudConflictModal
            remoteUpdatedAt={cloudConflict.remoteUpdatedAt}
            onPullCloud={() => {
              setTree(cloudConflict.remoteData);
              cloudSetLastSyncTs(storageKey, cloudConflict.remoteUpdatedAt);
              setCloudPushReadyKey(storageKey);
              setCloudConflict(null);
            }}
            onKeepLocal={() => {
              // Override the remote with our local copy. Flush forces an
              // immediate push instead of waiting for the debounce window.
              cloudSchedulePush(storageKey, treeRef.current);
              cloudFlushPush(storageKey);
              setCloudPushReadyKey(storageKey);
              setCloudConflict(null);
            }}
          />
        )}
        {migrationPrompt && (
          <MigrationModal
            count={migrationPrompt.count}
            onConfirm={runMigration}
            onSkip={skipMigration}
          />
        )}
        {migrationProgress && (
          <MigrationProgressPill
            done={migrationProgress.done}
            total={migrationProgress.total}
          />
        )}
      </div>
     </LogContext.Provider>
    </ModeContext.Provider>
  );
}

// --- TreeView ---

function TreeView({ tree, meId, meName, onHeartClick, focusMeRef, ...handlers }) {
  const viewportRef = useRef(null);
  const canvasRef = useRef(null);
  const floatingHeartRef = useRef(null);

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [transitioning, setTransitioning] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const zoomRef = useRef(zoom);
  const panRef = useRef(pan);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { panRef.current = pan; }, [pan]);

  const dragStartRef = useRef(null);
  const pinchStartRef = useRef(null);
  const transitionTimeoutRef = useRef(null);
  const rafRef = useRef(null);

  const scheduleTransition = (ms) => {
    // Clear any inline transition:'none' that cancelTransition may have set so
    // the .transitioning CSS class can take effect unobstructed.
    if (canvasRef.current) {
      canvasRef.current.style.transition = '';
    }
    setTransitioning(true);
    clearTimeout(transitionTimeoutRef.current);
    transitionTimeoutRef.current = setTimeout(() => setTransitioning(false), ms);
  };

  // Kill any in-flight CSS transition synchronously. Call this from any
  // user-initiated pan/zoom gesture so updates snap to the finger/cursor
  // instead of LERPing through a leftover reset/focus animation.
  // We also forcibly set transition:'none' directly on the DOM element so
  // the browser cancels the animation in the current frame — setTransitioning
  // is async (deferred to the next React render) and would otherwise let one
  // or more pan/zoom updates bleed through the still-active CSS transition.
  const cancelTransition = () => {
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
    // Synchronous DOM write — kills the transition before the next style flush.
    if (canvasRef.current) {
      canvasRef.current.style.transition = 'none';
    }
    setTransitioning(false);
  };

  const fitToView = useCallback((animate) => {
    const canvas = canvasRef.current;
    const viewport = viewportRef.current;
    if (!canvas || !viewport) return;
    const nw = canvas.scrollWidth;
    const nh = canvas.scrollHeight;
    const vw = viewport.clientWidth;
    const vh = viewport.clientHeight;
    if (nw === 0 || nh === 0 || vw === 0 || vh === 0) return;
    const targetZoom = clamp(
      Math.min((vw - FIT_PADDING * 2) / nw, (vh - FIT_PADDING * 2) / nh),
      MIN_ZOOM,
      1
    );
    const targetPan = {
      x: (vw - nw * targetZoom) / 2,
      y: FIT_PADDING,
    };
    if (animate) scheduleTransition(700);
    setZoom(targetZoom);
    setPan(targetPan);
  }, []);

  const focusMeAtCenter = useCallback(() => {
    if (!meId) return;
    const canvas = canvasRef.current;
    const viewport = viewportRef.current;
    if (!canvas || !viewport) return;

    // The LOD class on the canvas changes layout (card sizes, spacings) —
    // dot/mid/full mean different natural positions for the same node. To
    // land on-center after the focus, measure the layout under the LOD the
    // POST-focus render will use, not the current one. We synchronously
    // swap the class, force a layout flush, measure, then restore.
    const targetLod =
      HEART_FOCUS_ZOOM < 0.32 ? 'dot' : HEART_FOCUS_ZOOM < 0.7 ? 'mid' : 'full';
    const originalClass = canvas.className;
    const flippedClass = originalClass.replace(
      /\blod-\w+\b/,
      `lod-${targetLod}`
    );
    const needsFlip = flippedClass !== originalClass;
    if (needsFlip) canvas.className = flippedClass;

    const meEl = canvas.querySelector(`[data-person-id="${CSS.escape(meId)}"]`);
    if (!meEl) {
      if (needsFlip) canvas.className = originalClass;
      return;
    }

    const nodeRect = meEl.getBoundingClientRect();
    const viewportRect = viewport.getBoundingClientRect();
    const canvasRect = canvas.getBoundingClientRect();
    const naturalWidth = canvas.scrollWidth;
    if (!naturalWidth) {
      if (needsFlip) canvas.className = originalClass;
      return;
    }

    // Read the *actually applied* transform straight from the DOM. Avoids any
    // race between React's state, the ref-update useEffect, and rAF firing.
    const z = canvasRect.width / naturalWidth;
    const px = canvasRect.left - viewportRect.left;
    const py = canvasRect.top - viewportRect.top;

    // Convert node screen-center → canvas-natural coords. Horizontal stays
    // centered; vertical is anchored so the card's TOP lands TOP_INSET px
    // below the viewport top, leaving room for the floating heart above it.
    const screenCx = (nodeRect.left + nodeRect.right) / 2 - viewportRect.left;
    const naturalCx = (screenCx - px) / z;

    // Card top in natural canvas coords (subtract from current screen-top).
    const screenTop = nodeRect.top - viewportRect.top;
    const naturalTop = (screenTop - py) / z;
    const TOP_INSET = 90; // px between viewport top and card top after focus

    const targetPan = {
      x: viewportRect.width / 2 - naturalCx * HEART_FOCUS_ZOOM,
      y: TOP_INSET - naturalTop * HEART_FOCUS_ZOOM,
    };

    // Restore the class so React's upcoming render reconciles cleanly.
    if (needsFlip) canvas.className = originalClass;

    scheduleTransition(750);
    setZoom(HEART_FOCUS_ZOOM);
    setPan(targetPan);
  }, [meId]);

  // Expose focusMeAtCenter so TreeApp.goHome can fire same-tree focuses without
  // a remount.
  useEffect(() => {
    if (!focusMeRef) return;
    focusMeRef.current = focusMeAtCenter;
    return () => {
      if (focusMeRef.current === focusMeAtCenter) focusMeRef.current = null;
    };
  }, [focusMeRef, focusMeAtCenter]);

  useLayoutEffect(() => {
    // Drain the legacy focus flag if anything still writes it — but the
    // primary trigger is now meId, so the user lands centered on themselves
    // every login/refresh without needing the flag to be set first.
    sessionStorage.removeItem(HEART_FOCUS_FLAG);

    fitToView(false);

    // Defer the focus to the second next frame so the fit-state has actually
    // painted — that gives the CSS transition a real "from" position to
    // animate from, rather than React batching both updates into one paint.
    if (meId) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => focusMeAtCenter())
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitToView]);

  // Re-fit on viewport resize — the only case where overriding the user's
  // pan/zoom is the right call: phone rotation or window resize invalidates
  // their previous mental layout. Skip the first fire (that's just the
  // initial size delivery, already handled by the mount fit above) and
  // ignore tiny deltas like scrollbar toggles.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === 'undefined') return;
    let lastW = viewport.clientWidth;
    let lastH = viewport.clientHeight;
    let scheduled = null;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      if (Math.abs(width - lastW) < 20 && Math.abs(height - lastH) < 20) return;
      lastW = width;
      lastH = height;
      if (scheduled) cancelAnimationFrame(scheduled);
      scheduled = requestAnimationFrame(() => {
        scheduled = null;
        fitToView(false);
      });
    });
    ro.observe(viewport);
    return () => {
      ro.disconnect();
      if (scheduled) cancelAnimationFrame(scheduled);
    };
  }, [fitToView]);

  const setZoomCentered = useCallback((newZoom, animate = false) => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const z = zoomRef.current;
    const p = panRef.current;
    const clamped = clamp(newZoom, MIN_ZOOM, MAX_ZOOM);
    const cx = viewport.clientWidth / 2;
    const cy = viewport.clientHeight / 2;
    const ratio = clamped / z;
    if (animate) scheduleTransition(320);
    setZoom(clamped);
    setPan({ x: cx - (cx - p.x) * ratio, y: cy - (cy - p.y) * ratio });
  }, []);

  const onWheel = useCallback((e) => {
    e.preventDefault();
    cancelTransition();
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const deltaY = e.deltaY;
    const clientX = e.clientX;
    const clientY = e.clientY;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const el = viewportRef.current;
      if (!el) return;
      const z = zoomRef.current;
      const p = panRef.current;
      const factor = Math.exp(-deltaY * 0.0015);
      const newZoom = clamp(z * factor, MIN_ZOOM, MAX_ZOOM);
      const rect = el.getBoundingClientRect();
      const cx = clientX - rect.left;
      const cy = clientY - rect.top;
      const ratio = newZoom / z;
      setZoom(newZoom);
      setPan({ x: cx - (cx - p.x) * ratio, y: cy - (cy - p.y) * ratio });
    });
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const isInteractive = (target) =>
      target instanceof Element &&
      target.closest('input, button, label.photo-wrapper');

    const onTouchStart = (e) => {
      if (e.touches.length === 2 || e.touches.length === 1) cancelTransition();
      if (e.touches.length === 2) {
        const [t1, t2] = e.touches;
        const rect = el.getBoundingClientRect();
        const midX = (t1.clientX + t2.clientX) / 2 - rect.left;
        const midY = (t1.clientY + t2.clientY) / 2 - rect.top;
        // Capture the world (canvas-natural) coordinates that lie under the
        // initial finger midpoint. During move we keep that world point
        // pinned beneath the *current* midpoint so the gesture tracks the
        // fingers naturally — pinch-and-drag works without drift.
        pinchStartRef.current = {
          distance: Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY),
          zoom: zoomRef.current,
          worldX: (midX - panRef.current.x) / zoomRef.current,
          worldY: (midY - panRef.current.y) / zoomRef.current,
        };
        dragStartRef.current = null;
      } else if (e.touches.length === 1 && !isInteractive(e.target)) {
        const t = e.touches[0];
        dragStartRef.current = {
          x: t.clientX,
          y: t.clientY,
          panX: panRef.current.x,
          panY: panRef.current.y,
        };
        setIsDragging(true);
      }
    };

    const onTouchMove = (e) => {
      const start = pinchStartRef.current;
      if (e.touches.length === 2 && start) {
        e.preventDefault();
      } else if (e.touches.length === 1 && dragStartRef.current) {
        e.preventDefault();
      } else {
        return;
      }
      const touches = Array.from(e.touches).map((t) => ({
        clientX: t.clientX,
        clientY: t.clientY,
      }));
      const touchCount = e.touches.length;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const currentEl = viewportRef.current;
        if (!currentEl) return;
        const currentStart = pinchStartRef.current;
        if (touchCount === 2 && currentStart) {
          const [t1, t2] = touches;
          const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
          const rect = currentEl.getBoundingClientRect();
          // Recompute the midpoint from live touch positions every frame so
          // that combined pinch-and-drag does not drift.
          const curMidX = (t1.clientX + t2.clientX) / 2 - rect.left;
          const curMidY = (t1.clientY + t2.clientY) / 2 - rect.top;
          // Derive the world point currently under the live midpoint using the
          // most-recent pan/zoom values (not the stale gesture-start values).
          const currentZoom = zoomRef.current;
          const currentPan = panRef.current;
          const worldX = (curMidX - currentPan.x) / currentZoom;
          const worldY = (curMidY - currentPan.y) / currentZoom;
          // Scale delta is incremental: current distance vs previous frame's
          // distance so the anchor stays pinned under the live midpoint.
          const scaleDelta = dist / currentStart.distance;
          const newZoom = clamp(currentZoom * scaleDelta, MIN_ZOOM, MAX_ZOOM);
          // Update the ref so the next frame uses this frame's distance.
          pinchStartRef.current = { ...currentStart, distance: dist };
          setZoom(newZoom);
          setPan({
            x: curMidX - worldX * newZoom,
            y: curMidY - worldY * newZoom,
          });
        } else if (touchCount === 1 && dragStartRef.current) {
          const t = touches[0];
          setPan({
            x: dragStartRef.current.panX + (t.clientX - dragStartRef.current.x),
            y: dragStartRef.current.panY + (t.clientY - dragStartRef.current.y),
          });
        }
      });
    };

    const onTouchEnd = (e) => {
      if (e.touches.length < 2) pinchStartRef.current = null;
      if (e.touches.length === 0) {
        dragStartRef.current = null;
        setIsDragging(false);
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      if (!dragStartRef.current) return;
      const clientX = e.clientX;
      const clientY = e.clientY;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        if (!dragStartRef.current) return;
        setPan({
          x: dragStartRef.current.panX + (clientX - dragStartRef.current.x),
          y: dragStartRef.current.panY + (clientY - dragStartRef.current.y),
        });
      });
    };
    const onUp = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      dragStartRef.current = null;
      setIsDragging(false);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDragging]);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    if (e.target.closest('input, button, label.photo-wrapper')) return;
    e.preventDefault();
    cancelTransition();
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      panX: panRef.current.x,
      panY: panRef.current.y,
    };
    setIsDragging(true);
  }, []);

  // Cache the "me" DOM node so the positioning effect doesn't re-query on
  // every render — only re-query when meId changes.
  // Floating heart positioning. Query the Me-card straight from the DOM
  // each time rather than via a ref populated by a separate useEffect — the
  // ref-population effect runs *after* paint, but this layout effect runs
  // synchronously after render, so the ref was a frame behind on first
  // mount and the heart got stuck off-target until the next state change.
  const positionFloatingHeart = useCallback(() => {
    const el = floatingHeartRef.current;
    if (!el) return;
    if (!meId) {
      el.style.display = 'none';
      return;
    }
    const canvas = canvasRef.current;
    const viewport = viewportRef.current;
    if (!canvas || !viewport) {
      el.style.display = 'none';
      return;
    }
    const node = canvas.querySelector(`[data-person-id="${CSS.escape(meId)}"]`);
    if (!node) {
      el.style.display = 'none';
      return;
    }
    const rect = node.getBoundingClientRect();
    const vRect = viewport.getBoundingClientRect();
    const cx = (rect.left + rect.right) / 2 - vRect.left;
    const cy = rect.top - vRect.top - 6;
    const x = clamp(cx, HEART_HALF + 4, vRect.width - HEART_HALF - 4);
    const y = clamp(cy, HEART_HALF * 2 + 4, vRect.height - 6);
    el.style.display = '';
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
  }, [meId]);

  // Re-position on every state change that could move the Me-card visually.
  // tree included so adding/removing people shifts the heart with them.
  useLayoutEffect(() => {
    positionFloatingHeart();
  }, [positionFloatingHeart, zoom, pan, tree]);

  // The CSS transitions on .tree-canvas (fitToView, focusMe) finish without
  // any state change — listen for transitionend so the heart catches up to
  // the settled card position instead of staying at the in-flight rect.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onEnd = (e) => {
      if (e.target === canvas && e.propertyName === 'transform') {
        positionFloatingHeart();
      }
    };
    canvas.addEventListener('transitionend', onEnd);
    return () => canvas.removeEventListener('transitionend', onEnd);
  }, [positionFloatingHeart]);

  const lod = zoom < 0.32 ? 'dot' : zoom < 0.7 ? 'mid' : 'full';

  const heartLabel = meName ? `Back to ${meName}'s tree` : 'Back to your tree';

  return (
    <div
      ref={viewportRef}
      className={[
        'tree-viewport',
        isDragging ? 'dragging' : '',
        transitioning ? 'transitioning' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onMouseDown={handleMouseDown}
    >
      <div
        ref={canvasRef}
        className={`tree-canvas lod-${lod} ${transitioning ? 'transitioning' : ''}`}
        style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
      >
        <PersonSubtree person={tree} meId={meId} {...handlers} />
      </div>

      <button
        ref={floatingHeartRef}
        type="button"
        className="floating-heart"
        onClick={onHeartClick}
        onMouseDown={(e) => e.stopPropagation()}
        aria-label={heartLabel}
        title={heartLabel}
        style={{ display: 'none' }}
      >
        <HeartSvg filled size={26} strokeWidth={1.5} />
      </button>

      <ZoomControls
        zoom={zoom}
        onZoomChange={(z) => setZoomCentered(z, false)}
        onZoomIn={() => setZoomCentered(zoomRef.current * ZOOM_STEP, true)}
        onZoomOut={() => setZoomCentered(zoomRef.current / ZOOM_STEP, true)}
        onReset={() => fitToView(true)}
      />
    </div>
  );
}

function ZoomControls({ zoom, onZoomChange, onZoomIn, onZoomOut, onReset }) {
  return (
    <div className="zoom-controls" onMouseDown={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="zoom-btn"
        onClick={onZoomOut}
        aria-label="Zoom out"
        title="Zoom out"
      >
        −
      </button>
      <input
        type="range"
        className="zoom-slider"
        min={MIN_ZOOM}
        max={MAX_ZOOM}
        step="0.01"
        value={zoom}
        onChange={(e) => onZoomChange(parseFloat(e.target.value))}
        aria-label="Zoom level"
      />
      <button
        type="button"
        className="zoom-btn"
        onClick={onZoomIn}
        aria-label="Zoom in"
        title="Zoom in"
      >
        +
      </button>
      <span className="zoom-readout">{Math.round(zoom * 100)}%</span>
      <div className="zoom-divider" />
      <button
        type="button"
        className="zoom-btn reset-btn"
        onClick={onReset}
        aria-label="Reset view"
        title="Reset view"
      >
        ⟲
      </button>
    </div>
  );
}

// --- Tree node components ---

// Memoized — pan/zoom updates re-render the parent (TreeViewport) on every
// gesture event, but PersonSubtree's props (tree data, meId, useMemo'd
// handlers) are referentially stable across those updates, so React.memo
// skips the whole subtree's reconciliation during drags.
const PersonSubtree = memo(function PersonSubtree({
  person,
  meId,
  onInsertBefore,
  onInsertAfter,
  ...handlers
}) {
  const isRoot = person.id === handlers.rootId;
  return (
    <div className="subtree">
      {isRoot && (
        <button
          type="button"
          className="add-parents-btn"
          onClick={handlers.addParents}
        >
          + Add Parents
        </button>
      )}
      {/* Card row — the insert-sibling "+" buttons sit immediately to the
          left/right of the card itself rather than at the wrapping
          container's edges. The container expands to fit the widest
          descendants subtree below; pinning the buttons here keeps them
          visually attached to the card they apply to. */}
      <div className="subtree-card-row">
        {onInsertBefore && (
          <button
            type="button"
            className="sibling-insert-btn sibling-insert-left"
            onClick={onInsertBefore}
            aria-label="Insert sibling to the left"
            title="Insert sibling to the left"
          >
            +
          </button>
        )}
        <PersonNode
          person={person}
          isRoot={isRoot}
          isMe={person.id === meId}
          {...handlers}
        />
        {onInsertAfter && (
          <button
            type="button"
            className="sibling-insert-btn sibling-insert-right"
            onClick={onInsertAfter}
            aria-label="Insert sibling to the right"
            title="Insert sibling to the right"
          >
            +
          </button>
        )}
      </div>
      {person.marriages.length > 0 && (
        <div className="marriages">
          {person.marriages.map((m) => (
            <div
              key={m.id}
              className={`marriage-branch${m.ended ? ' is-ended' : ''}`}
            >
              <MarriageBranch
                marriage={m}
                lineageParent={person}
                meId={meId}
                {...handlers}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

function PersonNode({
  person,
  isRoot,
  isMe,
  updatePerson,
  addMarriage,
  deletePerson,
  setMe,
  openPersonMedia,
  openAuthPanel,
  openMemorial,
}) {
  const isDeceased = isDeceasedFromField(person.death);
  const hasAuth = !!person.auth?.email;
  return (
    <div
      className={`card person-card${isDeceased ? ' is-deceased' : ''}`}
      data-person-id={person.id}
    >
      <button
        type="button"
        className={`card-icon-btn heart-btn ${isMe ? 'is-me' : ''}`}
        onClick={() => setMe(person.id)}
        aria-label={isMe ? 'Unset as Me' : 'Set as Me'}
        title={isMe ? 'Unset as Me' : 'Set as Me'}
      >
        <HeartSvg filled={isMe} size={15} strokeWidth={2.2} />
      </button>
      {openAuthPanel && (
        <button
          type="button"
          className={`card-icon-btn key-btn${hasAuth ? ' has-auth' : ''}`}
          onClick={() => openAuthPanel(person.id)}
          aria-label={hasAuth ? 'Manage login' : 'Grant login access'}
          title={hasAuth ? 'Manage login' : 'Grant login access'}
        >
          <KeySvg size={13} />
        </button>
      )}
      {!isRoot && (
        <button
          type="button"
          className="card-icon-btn delete-btn"
          onClick={() => deletePerson(person.id)}
          aria-label="Delete person"
          title="Delete this person"
        >
          <TrashSvg size={14} />
        </button>
      )}
      <PersonFields
        person={person}
        updatePerson={updatePerson}
        placeholder="Name"
        openPersonMedia={openPersonMedia}
        openMemorial={openMemorial}
      />
      <button
        type="button"
        className="add-btn add-marriage-btn"
        onClick={() => addMarriage(person.id)}
      >
        + Add Marriage
      </button>
    </div>
  );
}

function MarriageBranch({
  marriage,
  lineageParent,
  meId,
  updatePerson,
  updateMarriage,
  addMarriage,
  addChild,
  addParents,
  deletePerson,
  deleteMarriage,
  onSpouseTrash,
  setMe,
  openPortal,
  openMarriageMedia,
  openPersonMedia,
  openAuthPanel,
  openMemorial,
  toggleMarriageEnded,
  rootId,
}) {
  const isSpouseMe = marriage.spouse.id === meId;
  const isSpouseDeceased = isDeceasedFromField(marriage.spouse.death);
  const spouseHasAuth = !!marriage.spouse.auth?.email;
  const isEnded = !!marriage.ended;
  return (
    <>
      <div className="marriage-pill">
        <span className="heart-glyph">❦</span>
        <input
          className="marriage-input"
          value={marriage.marriageYear}
          onChange={(e) =>
            updateMarriage(marriage.id, (m) => ({
              ...m,
              marriageYear: e.target.value,
            }))
          }
          placeholder="Married — Year"
        />
        <span className="heart-glyph">❦</span>
        <button
          type="button"
          className={`pill-ring-btn${isEnded ? ' is-ended' : ' is-active'}`}
          onClick={() => toggleMarriageEnded(marriage.id)}
          aria-label={isEnded ? 'Mark marriage as active' : 'Mark marriage as ended'}
          title={isEnded ? 'Marriage ended — click to mark as active' : 'Marriage active — click to mark as ended'}
        >
          <RingSvg active={!isEnded} size={13} />
        </button>
        <button
          type="button"
          className="pill-delete-btn"
          onClick={() => deleteMarriage(marriage.id)}
          aria-label="Delete marriage"
          title="Delete this marriage"
        >
          <TrashSvg size={11} />
        </button>
      </div>
      <MediaIconRow marriage={marriage} openMarriageMedia={openMarriageMedia} />
      <div
        className={`card spouse-card${isSpouseDeceased ? ' is-deceased' : ''}`}
        data-person-id={marriage.spouse.id}
      >
        <button
          type="button"
          className={`card-icon-btn heart-btn ${isSpouseMe ? 'is-me' : ''}`}
          onClick={() => setMe(marriage.spouse.id)}
          aria-label={isSpouseMe ? 'Unset as Me' : 'Set as Me'}
          title={isSpouseMe ? 'Unset as Me' : 'Set as Me'}
        >
          <HeartSvg filled={isSpouseMe} size={15} strokeWidth={2.2} />
        </button>
        <button
          type="button"
          className={`card-icon-btn leaf-btn ${marriage.spouse.isPortal ? 'is-portal' : ''}`}
          onClick={() => openPortal(marriage.spouse, { marriage, lineageParent })}
          aria-label={marriage.spouse.isPortal ? 'Open portal tree' : 'Make portal tree'}
          title={marriage.spouse.isPortal ? 'Open their family tree' : 'Make a portal to their tree'}
        >
          <LeafSvg size={15} />
        </button>
        {openAuthPanel && (
          <button
            type="button"
            className={`card-icon-btn key-btn${spouseHasAuth ? ' has-auth' : ''}`}
            onClick={() => openAuthPanel(marriage.spouse.id)}
            aria-label={spouseHasAuth ? 'Manage login' : 'Grant login access'}
            title={spouseHasAuth ? 'Manage login' : 'Grant login access'}
          >
            <KeySvg size={13} />
          </button>
        )}
        <button
          type="button"
          className="card-icon-btn delete-btn"
          onClick={() => onSpouseTrash(marriage.id)}
          aria-label="Replace or remove this person"
          title="Replace, edit, or remove this person"
        >
          <TrashSvg size={14} />
        </button>
        <PersonFields
          person={marriage.spouse}
          updatePerson={updatePerson}
          placeholder="Spouse name"
          openPersonMedia={openPersonMedia}
          openMemorial={openMemorial}
        />
        <div className="add-child-row">
          <button
            type="button"
            className="add-btn add-child-btn add-child-son"
            onClick={() => addChild(marriage.id, undefined, undefined, 'male')}
          >
            + Son
          </button>
          <button
            type="button"
            className="add-btn add-child-btn add-child-daughter"
            onClick={() => addChild(marriage.id, undefined, undefined, 'female')}
          >
            + Daughter
          </button>
        </div>
      </div>
      {marriage.children.length > 0 && (
        <div className="children">
          {marriage.children.map((c) => (
            <div key={c.id} className="child">
              <PersonSubtree
                person={c}
                meId={meId}
                onInsertBefore={() => addChild(marriage.id, c.id, 'before')}
                onInsertAfter={() => addChild(marriage.id, c.id, 'after')}
                updatePerson={updatePerson}
                updateMarriage={updateMarriage}
                addMarriage={addMarriage}
                addChild={addChild}
                addParents={addParents}
                deletePerson={deletePerson}
                deleteMarriage={deleteMarriage}
                onSpouseTrash={onSpouseTrash}
                setMe={setMe}
                openPortal={openPortal}
                openMarriageMedia={openMarriageMedia}
                openPersonMedia={openPersonMedia}
                openAuthPanel={openAuthPanel}
                openMemorial={openMemorial}
                toggleMarriageEnded={toggleMarriageEnded}
                rootId={rootId}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function MediaIconRow({ marriage, openMarriageMedia }) {
  const media = ensureMedia(marriage.media);
  const hasStory =
    stripHtml(media.story.vi).length > 0 || stripHtml(media.story.en).length > 0;
  const counts = {
    photos: media.photos.length,
    audio: media.audio.length,
    videos: media.videos.length,
    story: hasStory ? 1 : 0,
  };
  const items = [
    { type: 'photos', label: 'Photos', icon: <CameraSvg size={14} />, count: counts.photos },
    { type: 'audio',  label: 'Audio',  icon: <MusicSvg size={14} />,  count: counts.audio },
    { type: 'videos', label: 'Videos', icon: <FilmSvg size={14} />,   count: counts.videos },
    { type: 'story',  label: 'Story',  icon: <ScrollSvg size={14} />, count: counts.story },
  ];
  return (
    <div className="media-icons">
      {items.map((it) => (
        <button
          key={it.type}
          type="button"
          className={`media-icon-btn ${it.count > 0 ? 'has-content' : ''}`}
          onClick={() => openMarriageMedia(marriage.id, it.type)}
          aria-label={it.label}
          title={it.label + (it.count > 0 ? (it.type === 'story' ? '' : ` (${it.count})`) : '')}
        >
          {it.icon}
          {it.count > 0 && it.type !== 'story' && (
            <span className="media-count">{it.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

function PersonFields({ person, updatePerson, placeholder, openPersonMedia, openMemorial }) {
  const mode = useContext(ModeContext);
  const readOnly = mode === 'view';
  const isDeceased = isDeceasedFromField(person.death);
  const personalMedia = ensureMedia(person.media);
  const hasPersonalContent =
    personalMedia.photos.length > 0 ||
    personalMedia.audio.length > 0 ||
    personalMedia.videos.length > 0 ||
    stripHtml(personalMedia.story.vi).length > 0 ||
    stripHtml(personalMedia.story.en).length > 0;

  const onPhoto = async (file) => {
    if (!file) return;
    const blob = await compressImageToBlob(file, { maxDim: 480, quality: 0.85 });
    if (!blob) return;
    const prevPhoto = person.photo;
    const ref = await uploadPhotoOrLocal(blob, 'gia-pha/portraits');
    updatePerson(person.id, (p) => ({ ...p, photo: ref }));
    if (isIdbRef(prevPhoto)) idbDelete(idFromIdbRef(prevPhoto)).catch(() => {});
  };

  const setField = (field, value) =>
    updatePerson(person.id, (p) => ({ ...p, [field]: value }));

  return (
    <div className="person-fields">
      <label className="photo-wrapper" title={readOnly ? person.name : 'Upload photo'}>
        {person.photo ? (
          <MediaImg
            src={person.photo}
            alt={person.name || 'person'}
            className={`photo${person.gender ? ' gender-' + person.gender : ''}`}
          />
        ) : (
          <div
            className={`photo placeholder${person.gender ? ' gender-' + person.gender : ''}`}
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" width="22" height="22">
              <path
                fill="currentColor"
                d="M12 12.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm0 1.5a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9Zm-7 7c0-3.5 3.5-5.5 7-5.5s7 2 7 5.5v.5H5v-.5Z"
              />
            </svg>
          </div>
        )}
        {!readOnly && (
          <input
            type="file"
            accept="image/*"
            className="photo-input"
            onChange={(e) => onPhoto(e.target.files?.[0])}
          />
        )}
      </label>
      {isDeceased && <IncenseSvg />}
      <input
        className="name"
        value={person.name}
        onChange={(e) => setField('name', e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
      />
      <div className="dates">
        <input
          className="date"
          value={person.birth}
          onChange={(e) => setField('birth', e.target.value)}
          placeholder="b. YYYY"
          readOnly={readOnly}
        />
        <input
          className="date"
          value={person.death}
          onChange={(e) => setField('death', e.target.value)}
          placeholder="d. (optional)"
          readOnly={readOnly}
        />
      </div>
      <div className="person-actions-row">
        {openPersonMedia && (
          <button
            type="button"
            className={`person-media-btn${hasPersonalContent ? ' has-content' : ''}`}
            onClick={() => openPersonMedia(person.id)}
            aria-label="Personal media"
            title="Personal photos, audio, videos, story"
          >
            <PersonalMediaSvg size={12} />
          </button>
        )}
        {isDeceased && openMemorial && (
          <button
            type="button"
            className="memorial-btn"
            onClick={() => openMemorial(person.id)}
            aria-label="Open memorial"
            title="Open memorial"
          >
            <AltarIconSvg size={13} />
          </button>
        )}
        {!readOnly && (
          <button
            type="button"
            className={`gender-btn gender-btn-${person.gender || 'none'}`}
            onClick={() => {
              const next =
                !person.gender ? 'male' : person.gender === 'male' ? 'female' : null;
              updatePerson(person.id, (p) => ({ ...p, gender: next }));
            }}
            aria-label="Cycle gender (male / female / unset)"
            title={
              person.gender === 'male'
                ? 'Male — click to set female'
                : person.gender === 'female'
                ? 'Female — click to clear'
                : 'Set gender (male / female)'
            }
          >
            {person.gender === 'male' ? '♂' : person.gender === 'female' ? '♀' : '∘'}
          </button>
        )}
      </div>
    </div>
  );
}

// --- Media modal ---

function MediaModal({
  kind,
  id,
  initialType,
  tree,
  setMarriageMedia,
  setPersonMedia,
  onClose,
}) {
  const mode = useContext(ModeContext);
  const [tab, setTab] = useState(initialType || 'photos');

  // The active tab content (currently only StoryEditor) can register a guard
  // that intercepts close attempts when there are unsaved edits.
  const closeGuardRef = useRef(null);

  const handleCloseAttempt = useCallback(() => {
    const guard = closeGuardRef.current;
    if (guard) guard(onClose);
    else onClose();
  }, [onClose]);

  const node = useMemo(
    () => (kind === 'marriage' ? findMarriage(tree, id) : findPerson(tree, id)),
    [tree, kind, id]
  );

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') handleCloseAttempt(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleCloseAttempt]);

  const update = useCallback(
    (mediaUpdater) => {
      if (kind === 'marriage') setMarriageMedia(id, mediaUpdater);
      else setPersonMedia(id, mediaUpdater);
    },
    [kind, id, setMarriageMedia, setPersonMedia]
  );

  // Refs must come before any early return — Hook order has to be stable.
  const sheetRef = useRef(null);
  const dragStateRef = useRef(null);

  if (!node) return null;
  const media = ensureMedia(node.media);
  const isStory = tab === 'story';

  // Pass to media child components so their logEvent calls include where
  // the upload landed — "added audio to the marriage between X & Y" rather
  // than a bare "Uploaded audio".
  const mediaTarget =
    kind === 'marriage'
      ? { kind: 'marriage_media', id, name: marriageDisplayName(tree, id) }
      : { kind: 'person_media', id, name: node.name?.trim() || 'unnamed' };

  const tabs = [
    { type: 'photos', label: 'Photos', icon: <CameraSvg size={13} /> },
    { type: 'audio',  label: 'Audio',  icon: <MusicSvg size={13} /> },
    { type: 'videos', label: 'Videos', icon: <FilmSvg size={13} /> },
    { type: 'story',  label: 'Story',  icon: <ScrollSvg size={13} /> },
  ];

  // Drag-to-dismiss for the bottom-sheet variant. Only active on mobile
  // (the drag handle is display: none above 768 px) so the touch-listener
  // installation is harmless on desktop. Tracks the initial finger Y,
  // translates the modal downward on touchmove, and either closes the
  // modal or snaps back to zero on touchend depending on threshold.
  const onSheetTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    dragStateRef.current = { startY: e.touches[0].clientY, delta: 0 };
    if (sheetRef.current) sheetRef.current.classList.add('is-dragging');
  };
  const onSheetTouchMove = (e) => {
    const s = dragStateRef.current;
    if (!s) return;
    const delta = e.touches[0].clientY - s.startY;
    s.delta = delta;
    if (sheetRef.current) {
      // Only translate downward; ignore upward drags.
      sheetRef.current.style.setProperty('--sheet-drag-y', `${Math.max(0, delta)}px`);
    }
  };
  const onSheetTouchEnd = () => {
    const s = dragStateRef.current;
    dragStateRef.current = null;
    if (!sheetRef.current) return;
    sheetRef.current.classList.remove('is-dragging');
    if (s && s.delta > 100) {
      // Past threshold — let the snap-down animate, then close.
      sheetRef.current.style.setProperty('--sheet-drag-y', '100%');
      setTimeout(() => handleCloseAttempt(), 180);
    } else {
      sheetRef.current.style.setProperty('--sheet-drag-y', '0px');
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleCloseAttempt}>
      <div
        ref={sheetRef}
        className={`media-modal ${isStory ? 'media-modal-story' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="media-modal-drag-handle"
          aria-hidden="true"
          onTouchStart={onSheetTouchStart}
          onTouchMove={onSheetTouchMove}
          onTouchEnd={onSheetTouchEnd}
          onTouchCancel={onSheetTouchEnd}
        />
        <div className="media-modal-header">
          <div className="media-modal-tabs" role="tablist">
            {tabs.map((t) => (
              <button
                key={t.type}
                type="button"
                role="tab"
                aria-selected={tab === t.type}
                className={`media-tab${tab === t.type ? ' active' : ''}`}
                onClick={() => setTab(t.type)}
              >
                {t.icon}
                <span className="media-tab-label">{t.label}</span>
              </button>
            ))}
          </div>
          <button
            type="button"
            className="media-modal-close"
            onClick={handleCloseAttempt}
            aria-label="Close"
          >
            <CloseSvg size={18} />
          </button>
        </div>
        <div className="media-modal-body">
          {tab === 'photos' && (
            <PhotoAlbum media={media} update={update} mode={mode} mediaTarget={mediaTarget} />
          )}
          {tab === 'audio' && (
            <AudioList media={media} update={update} mode={mode} mediaTarget={mediaTarget} />
          )}
          {tab === 'videos' && (
            <VideoList media={media} update={update} mode={mode} mediaTarget={mediaTarget} />
          )}
          {tab === 'story' && (
            <StoryEditor
              story={ensureStory(media.story)}
              setStory={(updater) =>
                update((m) => ({
                  ...m,
                  story:
                    typeof updater === 'function'
                      ? updater(ensureStory(m.story))
                      : updater,
                }))
              }
              mode={mode}
              onClose={onClose}
              closeGuardRef={closeGuardRef}
              mediaTarget={mediaTarget}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function PhotoAlbum({ media, update, mode, mediaTarget }) {
  const inputRef = useRef(null);
  const logEvent = useContext(LogContext);
  const onAdd = async (files) => {
    if (!files || files.length === 0) return;
    const count = files.length;
    for (const file of Array.from(files)) {
      const blob = await compressImageToBlob(file, { maxDim: 1400, quality: 0.82 });
      if (!blob) continue;
      const ref = await uploadPhotoOrLocal(blob, 'gia-pha/albums');
      update((m) => ({
        ...m,
        photos: [...m.photos, { id: uuid(), src: ref, caption: '' }],
      }));
    }
    logEvent('upload_photo', mediaTarget || null, { count });
  };
  const setCaption = (id, caption) =>
    update((m) => ({
      ...m,
      photos: m.photos.map((p) => (p.id === id ? { ...p, caption } : p)),
    }));
  const remove = (id) =>
    update((m) => {
      const removed = m.photos.find((p) => p.id === id);
      if (removed && isIdbRef(removed.src)) {
        idbDelete(idFromIdbRef(removed.src)).catch(() => {});
      }
      return { ...m, photos: m.photos.filter((p) => p.id !== id) };
    });

  return (
    <div className="photo-album">
      {media.photos.length === 0 && (
        <p className="empty-note">
          {mode === 'edit' ? 'No photos yet — add some below.' : 'No photos in this album.'}
        </p>
      )}
      <div className="photo-grid">
        {media.photos.map((p) => (
          <div key={p.id} className="album-item">
            <MediaImg src={p.src} alt={p.caption || 'family photo'} className="album-photo" />
            {mode === 'edit' ? (
              <>
                <input
                  className="album-caption-input"
                  value={p.caption}
                  onChange={(e) => setCaption(p.id, e.target.value)}
                  placeholder="Caption…"
                />
                <button
                  type="button"
                  className="album-remove-btn"
                  onClick={() => remove(p.id)}
                  aria-label="Remove photo"
                  title="Remove photo"
                >
                  <CloseSvg size={14} />
                </button>
              </>
            ) : (
              p.caption && <p className="album-caption">{p.caption}</p>
            )}
          </div>
        ))}
      </div>
      {mode === 'edit' && (
        <div className="media-add-row">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => { onAdd(e.target.files); e.target.value = ''; }}
          />
          <button
            type="button"
            className="media-add-btn"
            onClick={() => inputRef.current?.click()}
          >
            <PlusSvg size={14} /> Add Photos
          </button>
        </div>
      )}
    </div>
  );
}

function AudioList({ media, update, mode, mediaTarget }) {
  const inputRef = useRef(null);
  const logEvent = useContext(LogContext);
  const onAdd = async (files) => {
    if (!files || files.length === 0) return;
    const count = files.length;
    for (const file of Array.from(files)) {
      const ref = await uploadAVOrLocal(file, 'audio');
      update((m) => ({
        ...m,
        audio: [...m.audio, {
          id: uuid(),
          src: ref.src,
          key: ref.key || undefined,
          title: file.name,
        }],
      }));
    }
    logEvent('upload_audio', mediaTarget || null, { count });
  };
  const setTitle = (id, title) =>
    update((m) => ({
      ...m,
      audio: m.audio.map((a) => (a.id === id ? { ...a, title } : a)),
    }));
  const remove = (id) =>
    update((m) => {
      const removed = m.audio.find((a) => a.id === id);
      if (removed) {
        if (isIdbRef(removed.src)) {
          idbDelete(idFromIdbRef(removed.src)).catch(() => {});
        } else {
          // Cloud cleanup is best-effort. Prefer the key captured at upload
          // time; fall back to deriving it from the URL for entries created
          // before keys were stored.
          cloudDeleteAV(removed.key || removed.src);
        }
      }
      return { ...m, audio: m.audio.filter((a) => a.id !== id) };
    });

  return (
    <div className="media-list">
      {media.audio.length === 0 && (
        <p className="empty-note">
          {mode === 'edit' ? 'No audio yet — upload below.' : 'No audio recordings.'}
        </p>
      )}
      <div className="audio-grid">
        {media.audio.map((a) => (
          <div key={a.id} className="audio-card">
            <div className="audio-cover">
              <MusicSvg size={42} />
            </div>
            {mode === 'edit' ? (
              <input
                className="media-title-input audio-title-input"
                value={a.title}
                onChange={(e) => setTitle(a.id, e.target.value)}
                placeholder="Title…"
              />
            ) : (
              <p className="media-title audio-title">{a.title || 'Untitled'}</p>
            )}
            <MediaAudio controls src={a.src} className="audio-player-compact" />
            {mode === 'edit' && (
              <button
                type="button"
                className="album-remove-btn"
                onClick={() => remove(a.id)}
                aria-label="Remove audio"
                title="Remove"
              >
                <CloseSvg size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
      {mode === 'edit' && (
        <div className="media-add-row">
          <input
            ref={inputRef}
            type="file"
            accept="audio/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => { onAdd(e.target.files); e.target.value = ''; }}
          />
          <button
            type="button"
            className="media-add-btn"
            onClick={() => inputRef.current?.click()}
          >
            <PlusSvg size={14} /> Add Audio
          </button>
        </div>
      )}
    </div>
  );
}

// Compress an image File to a JPEG Blob, capped at maxDim on the longest side.
// Falls back to the original File if anything in the canvas pipeline fails.
function compressImageToBlob(file, { maxDim = 1200, quality = 0.82 } = {}) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    const cleanup = () => URL.revokeObjectURL(url);
    img.onload = () => {
      try {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        if (!w || !h) { cleanup(); return resolve(file); }
        const scale = Math.min(1, maxDim / Math.max(w, h));
        const cw = Math.round(w * scale);
        const ch = Math.round(h * scale);
        const canvas = document.createElement('canvas');
        canvas.width = cw;
        canvas.height = ch;
        canvas.getContext('2d').drawImage(img, 0, 0, cw, ch);
        canvas.toBlob(
          (blob) => {
            cleanup();
            // Use the smaller of the two so we never make a file larger.
            if (!blob) return resolve(file);
            resolve(blob.size < file.size ? blob : file);
          },
          'image/jpeg',
          quality
        );
      } catch (e) { cleanup(); resolve(file); }
    };
    img.onerror = () => { cleanup(); resolve(file); };
    img.src = url;
  });
}

// Extract a JPEG thumbnail Blob from a video source URL (object URL, data
// URL, or IDB-resolved blob URL). Returns null on any failure.
function extractVideoThumbnailBlob(srcUrl) {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.src = srcUrl;
    video.muted = true;
    video.preload = 'metadata';
    video.crossOrigin = 'anonymous';
    let done = false;
    const finish = (result) => {
      if (done) return;
      done = true;
      resolve(result);
    };
    video.addEventListener('loadeddata', () => {
      try {
        const seekTo = Math.min(0.5, (video.duration || 1) / 2);
        video.currentTime = seekTo;
      } catch (e) { finish(null); }
    });
    video.addEventListener('seeked', () => {
      try {
        const canvas = document.createElement('canvas');
        const w = video.videoWidth || 320;
        const h = video.videoHeight || 240;
        const scale = Math.min(1, 400 / w);
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => finish(blob), 'image/jpeg', 0.7);
      } catch (e) { finish(null); }
    });
    video.addEventListener('error', () => finish(null));
    setTimeout(() => finish(null), 8000); // safety timeout
  });
}

function VideoList({ media, update, mode, mediaTarget }) {
  const inputRef = useRef(null);
  const logEvent = useContext(LogContext);
  const [ytFormOpen, setYtFormOpen] = useState(false);
  const [ytUrl, setYtUrl] = useState('');
  const [ytError, setYtError] = useState(false);
  const [playing, setPlaying] = useState(null);

  // One uploaded video file per node; YouTube links remain unlimited. The
  // kind heuristic mirrors the render path: explicit `kind`, else fall back
  // to "youtube if there's a url, otherwise file".
  const hasFileVideo = media.videos.some(
    (v) => (v.kind || (v.url ? 'youtube' : 'file')) === 'file'
  );

  const onAdd = async (files) => {
    if (!files || files.length === 0) return;
    // Defense-in-depth: even if the picker is somehow opened, only accept
    // the first file and only when no file video exists yet.
    if (hasFileVideo) return;
    const file = files[0];
    const objectUrl = URL.createObjectURL(file);
    let thumbBlob = null;
    try {
      thumbBlob = await extractVideoThumbnailBlob(objectUrl);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
    const videoRef = await uploadAVOrLocal(file, 'video');
    const thumbRef = thumbBlob
      ? await uploadPhotoOrLocal(thumbBlob, 'gia-pha/video-thumbs')
      : '';
    update((m) => ({
      ...m,
      videos: [
        ...m.videos,
        {
          id: uuid(),
          kind: 'file',
          src: videoRef.src,
          key: videoRef.key || undefined,
          url: '',
          title: file.name,
          thumbnail: thumbRef,
        },
      ],
    }));
    logEvent('upload_video', mediaTarget || null, { count: 1 });
  };

  const onAddYoutube = () => {
    const trimmed = ytUrl.trim();
    if (!trimmed) return;
    if (!youtubeIdFromUrl(trimmed)) {
      setYtError(true);
      return;
    }
    update((m) => ({
      ...m,
      videos: [
        ...m.videos,
        { id: uuid(), kind: 'youtube', src: '', url: trimmed, title: '' },
      ],
    }));
    logEvent('add_youtube', mediaTarget || null, { url: trimmed });
    setYtUrl('');
    setYtError(false);
    setYtFormOpen(false);
  };

  const setTitle = (id, title) =>
    update((m) => ({
      ...m,
      videos: m.videos.map((v) => (v.id === id ? { ...v, title } : v)),
    }));
  const remove = (id) =>
    update((m) => {
      const removed = m.videos.find((v) => v.id === id);
      if (removed) {
        // IDB cleanup (offline-pending uploads).
        const idbIds = [];
        if (isIdbRef(removed.src)) idbIds.push(idFromIdbRef(removed.src));
        if (isIdbRef(removed.thumbnail)) idbIds.push(idFromIdbRef(removed.thumbnail));
        if (idbIds.length) idbDelete(idbIds).catch(() => {});
        // R2 cleanup for the video file. Cloudinary thumbnails are left
        // orphaned for now (Cloudinary deletes need API-secret access via a
        // separate Worker endpoint we haven't built).
        if (removed.src && !isIdbRef(removed.src) && removed.kind !== 'youtube') {
          cloudDeleteAV(removed.key || removed.src);
        }
      }
      return { ...m, videos: m.videos.filter((v) => v.id !== id) };
    });

  return (
    <div className="media-list">
      {media.videos.length === 0 && (
        <p className="empty-note">
          {mode === 'edit'
            ? 'No videos yet — upload a file or add a YouTube link below.'
            : 'No videos.'}
        </p>
      )}
      <div className="video-grid">
        {media.videos.map((v) => {
          const kind = v.kind || (v.url ? 'youtube' : 'file');
          let thumbnailUrl = null;
          if (kind === 'youtube') {
            const yid = youtubeIdFromUrl(v.url);
            if (yid) thumbnailUrl = `https://img.youtube.com/vi/${yid}/hqdefault.jpg`;
          } else if (v.thumbnail) {
            thumbnailUrl = v.thumbnail;
          }
          return (
            <div key={v.id} className="video-card">
              <button
                type="button"
                className="video-thumb-btn"
                onClick={() => setPlaying(v)}
                aria-label="Play"
              >
                {thumbnailUrl ? (
                  <MediaImg src={thumbnailUrl} alt={v.title || 'video'} className="video-thumb" />
                ) : (
                  <div className="video-thumb-placeholder">
                    <FilmSvg size={36} />
                  </div>
                )}
                <span className="video-play-overlay">
                  <span className="video-play-icon">▶</span>
                </span>
                {kind === 'youtube' && <span className="video-yt-badge">YT</span>}
              </button>
              {mode === 'edit' ? (
                <input
                  className="media-title-input"
                  value={v.title}
                  onChange={(e) => setTitle(v.id, e.target.value)}
                  placeholder={kind === 'youtube' ? 'YouTube title…' : 'Title…'}
                />
              ) : (
                <p className="media-title video-card-title">
                  {v.title || (kind === 'youtube' ? 'YouTube video' : 'Untitled')}
                </p>
              )}
              {mode === 'edit' && (
                <button
                  type="button"
                  className="album-remove-btn"
                  onClick={() => remove(v.id)}
                  aria-label="Remove video"
                  title="Remove"
                >
                  <CloseSvg size={14} />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {mode === 'edit' && (
        <div className="media-add-row video-add-row">
          {!hasFileVideo && (
            <>
              <input
                ref={inputRef}
                type="file"
                accept="video/*"
                style={{ display: 'none' }}
                onChange={(e) => { onAdd(e.target.files); e.target.value = ''; }}
              />
              <button
                type="button"
                className="media-add-btn"
                onClick={() => inputRef.current?.click()}
              >
                <PlusSvg size={14} /> Upload
              </button>
            </>
          )}
          {hasFileVideo && (
            <p className="media-note">
              Only one video file per person. Add more videos as YouTube links below.
            </p>
          )}
          <button
            type="button"
            className="media-add-btn"
            onClick={() => setYtFormOpen((s) => !s)}
          >
            <PlusSvg size={14} /> YouTube
          </button>
          {ytFormOpen && (
            <div className="youtube-form">
              <input
                type="url"
                className="youtube-input"
                placeholder="https://youtube.com/watch?v=…"
                value={ytUrl}
                onChange={(e) => { setYtUrl(e.target.value); setYtError(false); }}
                onKeyDown={(e) => { if (e.key === 'Enter') onAddYoutube(); }}
                autoFocus
              />
              <button type="button" className="media-add-btn" onClick={onAddYoutube}>
                Add
              </button>
              {ytError && <span className="youtube-error">Invalid YouTube URL</span>}
            </div>
          )}
        </div>
      )}
      {playing && <VideoOverlay video={playing} onClose={() => setPlaying(null)} />}
    </div>
  );
}

function VideoOverlay({ video, onClose }) {
  const kind = video.kind || (video.url ? 'youtube' : 'file');
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop video-overlay-backdrop" onClick={onClose}>
      <div className="video-overlay-content" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="media-modal-close video-overlay-close"
          onClick={onClose}
          aria-label="Close"
        >
          <CloseSvg size={18} />
        </button>
        {kind === 'youtube' ? (
          <iframe
            className="video-overlay-player"
            src={`https://www.youtube.com/embed/${youtubeIdFromUrl(video.url)}?autoplay=1`}
            title={video.title || 'YouTube video'}
            allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture; fullscreen; autoplay"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : (
          <MediaVideo
            controls
            autoPlay
            src={video.src}
            className="video-overlay-player"
          />
        )}
        {video.title && <p className="video-overlay-title">{video.title}</p>}
      </div>
    </div>
  );
}

function StoryEditor({ story, setStory, mode, onClose, closeGuardRef, mediaTarget }) {
  const logEvent = useContext(LogContext);
  const editorRef = useRef(null);
  // Internal draft — edits stay here until the user clicks Save. Discard just
  // closes the modal; the parent's story stays as it was when the modal opened.
  const [draft, setDraft] = useState(() => ({
    vi: story.vi || '',
    en: story.en || '',
  }));
  const [language, setLanguage] = useState(() => {
    if (story.vi?.trim()) return 'vi';
    if (story.en?.trim()) return 'en';
    return 'vi';
  });
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState(false);

  const debounceRef = useRef(null);
  const opIdRef = useRef(0);
  const savedRangeRef = useRef(null);
  const languageRef = useRef(language);
  const draftRef = useRef(draft);
  useEffect(() => { languageRef.current = language; }, [language]);
  useEffect(() => { draftRef.current = draft; }, [draft]);

  const isEdit = mode === 'edit';
  const isDirty =
    draft.vi !== (story.vi || '') || draft.en !== (story.en || '');

  // Track dirty state for the close-guard, which runs from outside React
  // (in MediaModal's close handler).
  const isDirtyRef = useRef(isDirty);
  useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);
  const [unsavedPrompt, setUnsavedPrompt] = useState(false);
  const continueCloseRef = useRef(null);

  useEffect(() => {
    if (!closeGuardRef) return;
    closeGuardRef.current = (continueClose) => {
      if (isDirtyRef.current) {
        continueCloseRef.current = continueClose;
        setUnsavedPrompt(true);
      } else {
        continueClose();
      }
    };
    return () => {
      if (closeGuardRef) closeGuardRef.current = null;
    };
  }, [closeGuardRef]);

  // Reset innerHTML on language switch (or mode flip), but NOT on every draft
  // change — that would clobber the user's cursor mid-typing.
  useLayoutEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = draftRef.current[language] || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, mode]);

  // Cancel any pending translation when the editor unmounts.
  useEffect(() => {
    const debounceRefLocal = debounceRef;
    const opIdRefLocal = opIdRef;
    return () => {
      clearTimeout(debounceRefLocal.current);
      opIdRefLocal.current++;
    };
  }, []);

  const captureSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (editorRef.current?.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range.cloneRange();
      }
    }
  }, []);

  const restoreSelection = useCallback(() => {
    editorRef.current?.focus();
    const range = savedRangeRef.current;
    if (range) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, []);

  const runTranslate = useCallback(async (html, sourceLang) => {
    const opId = ++opIdRef.current;
    const targetLang = sourceLang === 'vi' ? 'en' : 'vi';
    const plain = stripHtml(html);
    if (!plain) {
      setDraft((prev) => ({ ...prev, [targetLang]: '' }));
      return;
    }
    setTranslateError(false);
    setTranslating(true);
    try {
      const translated = await translateText(plain, sourceLang, targetLang);
      if (opIdRef.current !== opId) return;
      const newHtml = `<p>${escapeHtml(translated)}</p>`;
      setDraft((prev) => ({ ...prev, [targetLang]: newHtml }));
      // If the user has toggled to the target language and isn't actively
      // typing, sync the contentEditable's DOM with the fresh translation —
      // useLayoutEffect's [language, mode] deps wouldn't fire on a draft change.
      if (
        targetLang === languageRef.current &&
        editorRef.current &&
        document.activeElement !== editorRef.current
      ) {
        editorRef.current.innerHTML = newHtml;
      }
    } catch (e) {
      if (opIdRef.current === opId) setTranslateError(true);
    } finally {
      if (opIdRef.current === opId) setTranslating(false);
    }
  }, []);

  const handleContentChange = useCallback((html) => {
    const lang = languageRef.current;
    setDraft((prev) => ({ ...prev, [lang]: html }));
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runTranslate(html, lang), 1500);
  }, [runTranslate]);

  const handleSave = useCallback(() => {
    clearTimeout(debounceRef.current);
    opIdRef.current++; // invalidate any in-flight translation
    setStory(draftRef.current);
    logEvent('save_story', mediaTarget || null, {});
    onClose?.();
  }, [setStory, onClose, logEvent, mediaTarget]);

  const handleDiscard = useCallback(() => {
    clearTimeout(debounceRef.current);
    opIdRef.current++;
    onClose?.();
  }, [onClose]);

  const handlePromptSave = useCallback(() => {
    clearTimeout(debounceRef.current);
    opIdRef.current++;
    setStory(draftRef.current);
    logEvent('save_story', mediaTarget || null, {});
    setUnsavedPrompt(false);
    continueCloseRef.current?.();
    continueCloseRef.current = null;
  }, [setStory, logEvent, mediaTarget]);

  const handlePromptDiscard = useCallback(() => {
    clearTimeout(debounceRef.current);
    opIdRef.current++;
    setUnsavedPrompt(false);
    continueCloseRef.current?.();
    continueCloseRef.current = null;
  }, []);

  const handlePromptKeepEditing = useCallback(() => {
    setUnsavedPrompt(false);
    continueCloseRef.current = null;
  }, []);

  const exec = useCallback((cmd, arg) => {
    restoreSelection();
    document.execCommand(cmd, false, arg);
    captureSelection();
    if (editorRef.current) handleContentChange(editorRef.current.innerHTML);
  }, [restoreSelection, captureSelection, handleContentChange]);

  const switchLanguage = useCallback((lang) => {
    if (lang === language) return;
    // Drop any pending debounce for the old language.
    clearTimeout(debounceRef.current);
    setLanguage(lang);
  }, [language]);

  return (
    <div className="parchment-page">
      <div className="parchment-toolbar">
        <div className="parchment-lang-toggle" onMouseDown={(e) => e.preventDefault()}>
          <button
            type="button"
            className={`parchment-lang-btn ${language === 'vi' ? 'active' : ''}`}
            onClick={() => switchLanguage('vi')}
          >
            Tiếng Việt
          </button>
          <button
            type="button"
            className={`parchment-lang-btn ${language === 'en' ? 'active' : ''}`}
            onClick={() => switchLanguage('en')}
          >
            English
          </button>
        </div>
        {translating && <span className="parchment-status">translating…</span>}
        {!translating && translateError && (
          <span className="parchment-status error">translation unavailable</span>
        )}

        {isEdit && (
          <>
            <span className="parchment-toolbar-divider" />
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')} title="Bold"><b>B</b></button>
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('italic')} title="Italic"><i>I</i></button>
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('underline')} title="Underline"><u>U</u></button>
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('strikeThrough')} title="Strikethrough"><s>S</s></button>

            <span className="parchment-toolbar-divider" />
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('formatBlock', '<h2>')} title="Heading">H</button>
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('formatBlock', '<p>')} title="Paragraph">¶</button>
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertUnorderedList')} title="List">•</button>

            <span className="parchment-toolbar-divider" />
            <select
              className="parchment-select"
              defaultValue=""
              onMouseDown={captureSelection}
              onChange={(e) => {
                if (e.target.value) {
                  exec('fontName', e.target.value);
                  e.target.value = '';
                }
              }}
              title="Font"
            >
              <option value="" disabled>Font</option>
              <option value="'Cormorant Garamond', serif">Cormorant</option>
              <option value="'Be Vietnam Pro', sans-serif">Vietnam Pro</option>
              <option value="'Playfair Display', serif">Playfair</option>
              <option value="Georgia, serif">Georgia</option>
              <option value="'Courier New', monospace">Courier</option>
            </select>

            <select
              className="parchment-select"
              defaultValue=""
              onMouseDown={captureSelection}
              onChange={(e) => {
                if (e.target.value) {
                  exec('fontSize', e.target.value);
                  e.target.value = '';
                }
              }}
              title="Size"
            >
              <option value="" disabled>Size</option>
              <option value="2">Small</option>
              <option value="3">Normal</option>
              <option value="5">Large</option>
              <option value="6">XL</option>
            </select>

            <input
              type="color"
              className="parchment-color"
              defaultValue="#3b2410"
              onMouseDown={captureSelection}
              onChange={(e) => exec('foreColor', e.target.value)}
              title="Text color"
            />
          </>
        )}
      </div>
      <div
        ref={editorRef}
        className="parchment-content"
        contentEditable={isEdit}
        suppressContentEditableWarning
        spellCheck={isEdit}
        lang={language}
        onInput={() => editorRef.current && handleContentChange(editorRef.current.innerHTML)}
        onKeyUp={captureSelection}
        onMouseUp={captureSelection}
        onBlur={captureSelection}
        data-placeholder={
          isEdit
            ? language === 'vi'
              ? 'Viết câu chuyện gia đình…'
              : 'Write the family story…'
            : ''
        }
      />
      {isEdit && (
        <div className="parchment-footer">
          <button
            type="button"
            className="parchment-btn parchment-discard"
            onClick={handleDiscard}
            disabled={!isDirty}
          >
            Discard Changes
          </button>
          <button
            type="button"
            className="parchment-btn parchment-save"
            onClick={handleSave}
            disabled={!isDirty}
          >
            Save
          </button>
        </div>
      )}
      {unsavedPrompt && (
        <div className="modal-backdrop unsaved-backdrop" onClick={handlePromptKeepEditing}>
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <p className="modal-message">
              You have unsaved changes to this story. Save them, or discard?
            </p>
            <div className="modal-actions modal-actions-three">
              <button
                type="button"
                className="modal-btn modal-cancel"
                onClick={handlePromptKeepEditing}
              >
                Keep Editing
              </button>
              <button
                type="button"
                className="modal-btn modal-discard"
                onClick={handlePromptDiscard}
              >
                Discard
              </button>
              <button
                type="button"
                className="modal-btn modal-save"
                onClick={handlePromptSave}
                autoFocus
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
